/**
 * Expanded Student Notes from Curriculum SLO Data
 * Comprehensive study materials for students with images and detailed content
 * Last Updated: January 27, 2026
 */

// ============================================
// IMAGE RESOURCES FOR NOTES
// ============================================
export const noteImages = {
  // ICT Images
  motherboard: "https://images.unsplash.com/photo-1758159234965-9d259875cf35?w=800",
  cpuInstall: "https://images.unsplash.com/photo-1756801370266-f589801cedc3?w=800",
  cpuSocket: "https://images.unsplash.com/photo-1758159234917-0c76ccd29051?w=800",
  programming: "https://images.unsplash.com/photo-1557324232-b8917d3c3dcb?w=800",
  codeScreen: "https://images.unsplash.com/photo-1624953587687-daf255b6b80a?w=800",
  codeLaptop: "https://images.unsplash.com/photo-1544847558-3ccacb31ee7f?w=800",
  wifiNetwork: "https://images.unsplash.com/photo-1603899123005-4b772bef4f09?w=800",
  networkCables: "https://images.unsplash.com/photo-1650551183117-82607baf17c8?w=800",
  ethernetCable: "https://images.pexels.com/photos/415043/pexels-photo-415043.jpeg?w=800",
  serverRoom: "https://images.pexels.com/photos/2881232/pexels-photo-2881232.jpeg?w=800",
  
  // Chemistry Images
  molecules: "https://images.unsplash.com/photo-1707135719544-fd75c0e66151?w=800",
  atomStructure: "https://images.pexels.com/photos/13014236/pexels-photo-13014236.jpeg?w=800",
  labBeakers: "https://images.unsplash.com/photo-1761095596584-34731de3e568?w=800",
  chemExperiment: "https://images.pexels.com/photos/8443070/pexels-photo-8443070.jpeg?w=800",
  labEquipment: "https://images.pexels.com/photos/8544949/pexels-photo-8544949.jpeg?w=800",
  chemFlasks: "https://images.pexels.com/photos/8325721/pexels-photo-8325721.jpeg?w=800"
};

// ============================================
// YEAR 11 ICT STUDENT NOTES (EXPANDED)
// ============================================
export const year11ICTStudentNotes = {
  strand1: {
    title: "Personal Computer Systems",
    overview: "This strand covers the fundamental understanding of operating a desktop computer, managing computer files, and protecting them from corruption. You will learn about hardware components, operating systems, and basic computer maintenance.",
    image: noteImages.motherboard,
    units: [
      {
        id: "1.1",
        title: "Introduction to Computer Systems",
        image: noteImages.cpuInstall,
        objectives: [
          "Understand and name major hardware components of a PC",
          "Describe functions of CPU, RAM, storage devices, and peripherals",
          "Operate a PC using OS tools and utilities",
          "Manage files using file manager",
          "Identify internal and external computer components"
        ],
        keyTerms: [
          { term: "CPU (Central Processing Unit)", definition: "The brain of the computer that processes all instructions and performs calculations. It executes billions of operations per second." },
          { term: "RAM (Random Access Memory)", definition: "Temporary memory that stores data currently being used. It's fast but loses all data when power is off." },
          { term: "Hard Drive (HDD)", definition: "Permanent storage device using magnetic disks. Stores your files, programs, and operating system." },
          { term: "SSD (Solid State Drive)", definition: "Modern storage device using flash memory. Faster and more reliable than HDD, but more expensive." },
          { term: "Operating System (OS)", definition: "Software that manages computer hardware and provides user interface. Examples: Windows, macOS, Linux, Chrome OS." },
          { term: "GUI (Graphical User Interface)", definition: "Visual way to interact with a computer using icons, windows, menus, and pointer devices." },
          { term: "Peripheral", definition: "External device connected to a computer - keyboard, mouse, printer, scanner, webcam, etc." },
          { term: "Motherboard", definition: "The main circuit board that connects all computer components together. Contains CPU socket, RAM slots, and expansion slots." },
          { term: "BIOS/UEFI", definition: "Firmware that initializes hardware during startup before the operating system loads." },
          { term: "Port", definition: "Connection point for external devices - USB, HDMI, Ethernet, audio jacks." }
        ],
        studyNotes: `
## Introduction to Computer Systems

### What is a Computer?
A computer is an electronic device that accepts input, processes data, stores information, and produces output. Modern computers are programmable machines that can perform a wide variety of tasks.

### The Computer System Cycle
**INPUT → PROCESS → OUTPUT → STORAGE**

1. **Input**: Data enters the computer (keyboard typing, mouse clicks, file uploads)
2. **Process**: CPU manipulates the data according to instructions
3. **Output**: Results are displayed (screen, printer, speakers)
4. **Storage**: Data is saved for later use

---

## Major Hardware Components

### 1. The CPU (Central Processing Unit)
The CPU is the "brain" of the computer. It performs all calculations and executes program instructions.

**Key CPU Specifications:**
- **Clock Speed**: Measured in GHz (gigahertz). Higher = faster processing
- **Cores**: Modern CPUs have multiple cores (dual-core, quad-core, octa-core)
- **Cache**: Small, fast memory built into the CPU
- **Architecture**: 32-bit or 64-bit processing

**Popular CPU Manufacturers:**
- Intel (Core i3, i5, i7, i9)
- AMD (Ryzen 3, 5, 7, 9)

### 2. RAM (Random Access Memory)
RAM is your computer's short-term memory. It temporarily holds data that the CPU is actively using.

**Characteristics of RAM:**
- **Volatile**: Data is lost when power is turned off
- **Fast**: Much faster than hard drives
- **Measured in GB**: Typical computers have 4GB to 64GB
- **DDR Types**: DDR3, DDR4, DDR5 (newer = faster)

**How much RAM do you need?**
- Basic use: 4-8 GB
- Gaming/Design: 16-32 GB
- Professional work: 32-64 GB

### 3. Storage Devices

#### Hard Disk Drive (HDD)
- Uses spinning magnetic platters
- Cheaper per GB
- Slower read/write speeds
- More prone to physical damage

#### Solid State Drive (SSD)
- Uses flash memory (no moving parts)
- More expensive per GB
- Much faster read/write speeds
- More reliable and durable

#### Comparison Table:
| Feature | HDD | SSD |
|---------|-----|-----|
| Speed | 80-160 MB/s | 200-550 MB/s (SATA), 3500+ MB/s (NVMe) |
| Durability | Lower (moving parts) | Higher (no moving parts) |
| Price | ~$0.03/GB | ~$0.10/GB |
| Noise | Audible spinning | Silent |
| Boot Time | 30-60 seconds | 10-15 seconds |

### 4. Input Devices
Devices that send data TO the computer:
- **Keyboard**: Text and command input
- **Mouse**: Pointing and clicking
- **Scanner**: Converting physical documents to digital
- **Microphone**: Audio input
- **Webcam**: Video input
- **Touchscreen**: Touch input

### 5. Output Devices
Devices that receive data FROM the computer:
- **Monitor**: Visual display
- **Printer**: Physical document output
- **Speakers**: Audio output
- **Projector**: Large display output
- **Headphones**: Personal audio output

---

## Operating System Functions

The Operating System (OS) is the most important software on your computer. It manages:

1. **Hardware Management**
   - Controls CPU, memory, storage, and devices
   - Allocates resources to programs

2. **User Interface**
   - Desktop, icons, windows, menus
   - Command line for advanced users

3. **File Management**
   - Create, copy, move, delete files
   - Organize files in folders/directories

4. **Security**
   - User accounts and passwords
   - Protect against malware

5. **Running Applications**
   - Start and stop programs
   - Multitasking (running multiple programs)

### Popular Operating Systems:
- **Windows**: Most common for PCs (~75% market share)
- **macOS**: For Apple computers
- **Linux**: Open-source, free, used in servers
- **Chrome OS**: For Chromebooks, cloud-based

---

## File Management Best Practices

### Creating an Organized Folder Structure:
\`\`\`
Documents/
├── School/
│   ├── Year 11/
│   │   ├── ICT/
│   │   ├── Chemistry/
│   │   └── English/
│   └── Assignments/
├── Personal/
└── Downloads/
\`\`\`

### File Naming Conventions:
- Use descriptive names: "ICT_Assignment1_JohnSmith.docx"
- Avoid special characters: / \\ ? * : | " < >
- Include dates for versions: "Report_2026-01-27.pdf"

### Important File Extensions:
- Documents: .docx, .pdf, .txt
- Spreadsheets: .xlsx, .csv
- Images: .jpg, .png, .gif
- Audio: .mp3, .wav
- Video: .mp4, .avi
- Compressed: .zip, .rar

---

## Computer Maintenance

### Regular Maintenance Tasks:
1. **Update software** - Keep OS and programs current
2. **Run antivirus scans** - Check for malware weekly
3. **Clean temporary files** - Use Disk Cleanup
4. **Defragment HDD** - Organize files (not needed for SSD)
5. **Backup important files** - Use external drive or cloud

### Physical Maintenance:
- Keep computer in ventilated area
- Clean dust from vents and fans
- Use surge protector
- Shut down properly (don't just unplug)
        `,
        practiceQuestions: [
          "Name and describe the function of five major hardware components inside a computer.",
          "What is the difference between RAM and a hard drive? Why do computers need both?",
          "Explain the four stages of the computer system cycle with examples.",
          "Compare HDD and SSD storage devices. List three advantages of each.",
          "List four functions of an operating system and explain why each is important.",
          "What is a peripheral device? Give three examples of input peripherals and three output peripherals.",
          "Create a folder structure diagram for organizing your school files.",
          "Why is it important to perform regular computer maintenance? List three maintenance tasks.",
          "What does CPU clock speed mean? How does it affect computer performance?",
          "Explain what happens when you save a file. Which components are involved?"
        ],
        examTips: [
          "Draw and label diagrams of computer components - examiners love visual answers",
          "Always give examples when explaining concepts",
          "Remember the INPUT → PROCESS → OUTPUT → STORAGE cycle",
          "Know the difference between volatile and non-volatile storage"
        ]
      },
      {
        id: "1.2",
        title: "History of Computers",
        image: noteImages.motherboard,
        objectives: [
          "Know the names of famous computer inventors and their contributions",
          "Understand major milestones in computer development",
          "Trace the evolution from early machines to modern PCs",
          "Identify the five generations of computers",
          "Understand the impact of computing on society"
        ],
        keyTerms: [
          { term: "Charles Babbage", definition: "English mathematician (1791-1871) known as the 'Father of Computing'. Designed the Analytical Engine - the first general-purpose computer concept." },
          { term: "Ada Lovelace", definition: "English mathematician (1815-1852) considered the first computer programmer. Wrote the first algorithm for Babbage's machine." },
          { term: "Alan Turing", definition: "British mathematician (1912-1954), father of theoretical computer science. Created the Turing Machine concept and helped break Nazi codes in WWII." },
          { term: "ENIAC", definition: "Electronic Numerical Integrator and Computer (1945) - first general-purpose electronic computer. Weighed 30 tons and filled a room." },
          { term: "Microprocessor", definition: "Integrated circuit containing the entire CPU on a single chip. First commercial microprocessor: Intel 4004 (1971)." },
          { term: "Vacuum Tube", definition: "Electronic component used in first-generation computers. Large, generated lots of heat, and frequently failed." },
          { term: "Transistor", definition: "Semiconductor device that replaced vacuum tubes. Smaller, more reliable, and generated less heat." },
          { term: "Integrated Circuit (IC)", definition: "Multiple transistors on a single chip. Also called microchip. Enabled miniaturization of computers." },
          { term: "ARPANET", definition: "First computer network (1969), predecessor to the internet. Created by US Department of Defense." },
          { term: "Tim Berners-Lee", definition: "British scientist who invented the World Wide Web in 1989 at CERN." }
        ],
        studyNotes: `
## History of Computers

### Early Calculating Devices

#### The Abacus (3000 BC)
- First known calculating tool
- Used beads on rods to perform arithmetic
- Still used in some parts of the world today

#### Pascaline (1642)
- Mechanical calculator invented by Blaise Pascal
- Could add and subtract
- Used gears and wheels

#### Difference Engine & Analytical Engine (1820s-1830s)
- Designed by Charles Babbage
- Analytical Engine was the first concept of a programmable computer
- Had memory, processor, input, and output - like modern computers!
- Ada Lovelace wrote algorithms for it

---

## Key Inventors and Their Contributions

### Charles Babbage (1791-1871)
**"The Father of Computing"**
- Designed the Difference Engine (1822) - automatic calculator
- Designed the Analytical Engine (1837) - first general-purpose computer concept
- Never completed his machines due to funding and technology limitations
- His ideas were 100 years ahead of his time

### Ada Lovelace (1815-1852)
**"The First Programmer"**
- Daughter of poet Lord Byron
- Wrote detailed notes on Babbage's Analytical Engine
- Created the first algorithm intended for machine processing
- Recognized that computers could do more than just calculate numbers
- Programming language "Ada" named after her

### Alan Turing (1912-1954)
**"Father of Theoretical Computer Science"**
- Created the Turing Machine concept (1936) - theoretical model of computation
- Worked at Bletchley Park during WWII breaking Nazi Enigma codes
- Developed the Turing Test for artificial intelligence
- Considered a founder of AI and computer science
- The "Turing Award" is the highest honor in computing

### Other Important Figures:
- **John von Neumann** - Created the stored-program concept
- **Grace Hopper** - Developed first compiler and COBOL language
- **Steve Jobs & Steve Wozniak** - Founded Apple, created personal computers
- **Bill Gates** - Founded Microsoft, made PCs accessible to everyone

---

## The Five Generations of Computers

### First Generation (1940-1956): Vacuum Tubes
**Technology:** Vacuum tubes for circuitry
**Characteristics:**
- Huge size (room-filling)
- Generated enormous heat
- Frequent hardware failures
- Used punch cards for input
- Very expensive to operate

**Examples:** ENIAC, UNIVAC, EDVAC

**ENIAC Facts:**
- First general-purpose electronic computer
- 30 tons, 18,000 vacuum tubes
- Consumed 150 kilowatts of power
- Could perform 5,000 additions per second

### Second Generation (1956-1963): Transistors
**Technology:** Transistors replaced vacuum tubes
**Characteristics:**
- Smaller and more reliable
- Generated less heat
- Faster processing
- Used magnetic core memory
- Assembly language introduced

**Examples:** IBM 7090, UNIVAC 1108

### Third Generation (1964-1971): Integrated Circuits
**Technology:** ICs (chips) with multiple transistors
**Characteristics:**
- Even smaller and cheaper
- More reliable
- Keyboards and monitors introduced
- Operating systems developed
- High-level programming languages

**Examples:** IBM System/360, PDP-8

### Fourth Generation (1971-Present): Microprocessors
**Technology:** Entire CPU on a single chip
**Characteristics:**
- Personal computers possible
- GUIs (Graphical User Interfaces)
- Networking and Internet
- Portable devices (laptops, tablets)
- Massive storage capacity

**Key Milestone:** Intel 4004 (1971) - first microprocessor

**Examples:** All modern PCs, Macs, smartphones

### Fifth Generation (Present-Future): AI & Beyond
**Technology:** Artificial Intelligence, quantum computing
**Characteristics:**
- Machine learning and AI
- Voice recognition
- Natural language processing
- Parallel processing
- Quantum computers (experimental)

**Examples:** IBM Watson, Google AI, Quantum computers

---

## Timeline of Major Milestones

| Year | Milestone |
|------|-----------|
| 1837 | Babbage designs Analytical Engine |
| 1843 | Ada Lovelace writes first algorithm |
| 1936 | Turing creates Turing Machine concept |
| 1945 | ENIAC becomes operational |
| 1947 | Transistor invented at Bell Labs |
| 1958 | Integrated circuit invented |
| 1969 | ARPANET (first internet) created |
| 1971 | Intel 4004 microprocessor |
| 1975 | Microsoft founded |
| 1976 | Apple Computer founded |
| 1981 | IBM PC released |
| 1989 | World Wide Web invented |
| 1991 | Linux created |
| 1998 | Google founded |
| 2007 | iPhone released |
| 2010s | AI and machine learning boom |

---

## The Internet and World Wide Web

### ARPANET (1969)
- First packet-switching network
- Connected four universities initially
- Foundation for the modern internet

### World Wide Web (1989)
- Invented by Tim Berners-Lee at CERN
- Uses HTTP protocol and HTML language
- Made the internet accessible to everyone
- First website: info.cern.ch

### Internet vs World Wide Web
- **Internet**: Global network of connected computers
- **Web**: System of websites accessed via the internet
- The web is just one service on the internet (like email, FTP)
        `,
        practiceQuestions: [
          "Who is known as the 'Father of Computing' and what did he design?",
          "Explain Ada Lovelace's contribution to computing. Why is she important?",
          "Describe Alan Turing's contributions to computer science and his work during WWII.",
          "Name the five generations of computers and the main technology used in each.",
          "What was ENIAC? Describe its size, power consumption, and capabilities.",
          "Explain the difference between vacuum tubes, transistors, and integrated circuits.",
          "What is a microprocessor? Why was it revolutionary?",
          "Who invented the World Wide Web? What is the difference between the Internet and the Web?",
          "Create a timeline showing five important events in computer history.",
          "How have computers changed from the first generation to today? Give specific examples."
        ],
        examTips: [
          "Memorize key dates and names - they are frequently tested",
          "Understand WHY each generation was an improvement over the previous",
          "Be able to compare technologies: vacuum tubes vs transistors vs ICs",
          "Know the difference between Internet and World Wide Web"
        ]
      },
      {
        id: "1.3",
        title: "Programming I - Introduction",
        image: noteImages.programming,
        objectives: [
          "Understand the concept of programming and algorithms",
          "Learn the stages of program development",
          "Write basic algorithms using pseudocode",
          "Create and interpret flowcharts",
          "Understand variables and data types",
          "Identify different types of programming errors"
        ],
        keyTerms: [
          { term: "Algorithm", definition: "A step-by-step set of instructions to solve a specific problem. Like a recipe for cooking." },
          { term: "Program", definition: "A set of instructions written in a programming language that a computer can execute." },
          { term: "Pseudocode", definition: "Plain English description of program steps. Not actual code, but structured like code." },
          { term: "Flowchart", definition: "Visual diagram showing the flow of a program using standard symbols." },
          { term: "Variable", definition: "A named container that stores data. The value can change during program execution." },
          { term: "Data Type", definition: "The kind of data a variable can hold: integer, float, string, boolean, etc." },
          { term: "Syntax", definition: "The rules for writing correct code in a programming language. Like grammar in English." },
          { term: "Syntax Error", definition: "Error caused by violating programming language rules. The program won't run." },
          { term: "Logic Error", definition: "Error in program logic. Program runs but produces wrong results." },
          { term: "Runtime Error", definition: "Error that occurs while the program is running (e.g., division by zero)." },
          { term: "Debugging", definition: "The process of finding and fixing errors (bugs) in a program." },
          { term: "Compiler", definition: "Software that translates entire program code into machine language before running." },
          { term: "Interpreter", definition: "Software that translates and runs code line by line." }
        ],
        studyNotes: `
## Introduction to Programming

### What is Programming?
Programming is the process of writing instructions that tell a computer what to do. These instructions are written in a special language that computers can understand.

**Why Learn Programming?**
- Solve problems systematically
- Create useful applications
- Understand how technology works
- High-demand career skill
- Develops logical thinking

---

## The Program Development Cycle

### Six Stages of Program Development:

#### 1. Problem Definition
- Clearly understand what needs to be solved
- Define inputs, outputs, and requirements
- Ask: What should the program do?

#### 2. Program Design
- Plan how to solve the problem
- Write algorithms and pseudocode
- Create flowcharts
- Break complex problems into smaller parts

#### 3. Coding (Implementation)
- Write the actual program code
- Choose appropriate programming language
- Follow coding standards and conventions

#### 4. Testing
- Check if the program works correctly
- Test with different inputs
- Find and document errors

#### 5. Debugging
- Fix errors found during testing
- Re-test after each fix
- Verify the fix doesn't create new problems

#### 6. Documentation & Maintenance
- Write user guides and technical documentation
- Update program as needed
- Fix bugs reported by users

---

## Algorithms

### What is an Algorithm?
An algorithm is a sequence of clear, precise steps to solve a problem. Every computer program is based on one or more algorithms.

### Characteristics of a Good Algorithm:
1. **Clear and unambiguous** - Each step must be precise
2. **Well-ordered** - Steps in correct sequence
3. **Effective** - Each step can be performed
4. **Finite** - Must eventually end
5. **Input/Output defined** - Clear what goes in and comes out

### Example Algorithm: Making Tea
\`\`\`
1. Boil water
2. Put tea bag in cup
3. Pour boiling water into cup
4. Wait 3 minutes
5. Remove tea bag
6. Add milk and sugar if desired
7. Stir and serve
\`\`\`

---

## Pseudocode

### What is Pseudocode?
Pseudocode is a plain language description of program steps. It's not real code, but it's structured to look like code.

### Pseudocode Conventions:
- Keywords in CAPITALS: IF, THEN, ELSE, WHILE, FOR
- Indentation shows code blocks
- Use clear variable names
- Add comments for clarity

### Example 1: Calculate Sum of Two Numbers
\`\`\`
BEGIN
    INPUT number1
    INPUT number2
    SET sum = number1 + number2
    OUTPUT "The sum is: " + sum
END
\`\`\`

### Example 2: Check if Number is Positive
\`\`\`
BEGIN
    INPUT number
    IF number > 0 THEN
        OUTPUT "Number is positive"
    ELSE IF number < 0 THEN
        OUTPUT "Number is negative"
    ELSE
        OUTPUT "Number is zero"
    ENDIF
END
\`\`\`

### Example 3: Count from 1 to 10
\`\`\`
BEGIN
    SET counter = 1
    WHILE counter <= 10
        OUTPUT counter
        SET counter = counter + 1
    ENDWHILE
END
\`\`\`

---

## Flowcharts

### Standard Flowchart Symbols:

| Symbol | Shape | Meaning |
|--------|-------|---------|
| Terminal | Oval/Rounded Rectangle | Start or End of program |
| Process | Rectangle | Action or calculation |
| Decision | Diamond | Yes/No question |
| Input/Output | Parallelogram | Input or output operation |
| Flow Line | Arrow | Direction of flow |
| Connector | Circle | Connect parts of flowchart |

### Flowchart Rules:
1. Start with a terminal symbol labeled "Start"
2. End with a terminal symbol labeled "End"
3. Arrows show direction of flow
4. Only one flow line enters a symbol
5. Decision diamonds have two exit paths (Yes/No)

### Example Flowchart: Is Number Even or Odd?
\`\`\`
    [Start]
        ↓
    [Input number]
        ↓
    <Is number % 2 = 0?>
        ↓               ↓
       Yes              No
        ↓               ↓
  [Output "Even"]  [Output "Odd"]
        ↓               ↓
        └───────┬───────┘
                ↓
            [End]
\`\`\`

---

## Variables and Data Types

### What is a Variable?
A variable is a named storage location in memory. Think of it as a labeled box that holds a value.

**Variable characteristics:**
- Has a name (identifier)
- Has a data type
- Stores a value
- Value can change during execution

### Common Data Types:

| Data Type | Description | Examples |
|-----------|-------------|----------|
| Integer | Whole numbers | 1, -5, 100, 0 |
| Float/Real | Decimal numbers | 3.14, -2.5, 0.001 |
| String | Text | "Hello", "Vanuatu", "123" |
| Boolean | True or False | True, False |
| Character | Single letter | 'A', 'x', '?' |

### Variable Naming Rules:
- Start with a letter or underscore
- Can contain letters, numbers, underscores
- Cannot be a reserved keyword
- Should be descriptive

**Good names:** studentAge, totalScore, firstName
**Bad names:** x, temp, data123

---

## Types of Errors

### 1. Syntax Errors
- Caused by breaking programming language rules
- Program won't run at all
- Easy to find - compiler/interpreter reports them

**Examples:**
\`\`\`
pint("Hello")       // Wrong: 'pint' instead of 'print'
if x = 5            // Wrong: missing 'then' or ':'
\`\`\`

### 2. Logic Errors
- Program runs but gives wrong answer
- Most difficult to find
- Caused by incorrect algorithm

**Example:**
\`\`\`
average = num1 + num2 + num3 / 3    // Wrong: only num3 divided by 3
average = (num1 + num2 + num3) / 3  // Correct: all divided by 3
\`\`\`

### 3. Runtime Errors
- Occur while program is running
- Often caused by unexpected input
- Program crashes or stops

**Examples:**
- Division by zero
- File not found
- Out of memory
- Array index out of bounds

### Debugging Strategies:
1. Read error messages carefully
2. Use print statements to trace values
3. Test with simple inputs first
4. Check boundary conditions
5. Review logic step by step
        `,
        practiceQuestions: [
          "What is an algorithm? List five characteristics of a good algorithm.",
          "Explain the six stages of program development.",
          "Write pseudocode for a program that finds the larger of two numbers.",
          "Draw a flowchart for a program that calculates the area of a rectangle (length × width).",
          "What is the difference between a syntax error and a logic error? Give an example of each.",
          "Define the term 'variable'. Name four common data types with examples.",
          "Write pseudocode for a program that calculates the average of three test scores.",
          "What is debugging? Describe three strategies for finding errors in programs.",
          "Explain the difference between a compiler and an interpreter.",
          "Draw a flowchart for a program that prints numbers 1 to 5 using a loop."
        ],
        examTips: [
          "Practice drawing flowcharts with correct symbols",
          "Always use proper pseudocode conventions",
          "Learn to trace through algorithms step by step",
          "Know the three types of errors and how to identify them"
        ]
      }
    ]
  },
  strand2: {
    title: "Application Software",
    overview: "This strand covers using productivity software including word processing, spreadsheets, presentations, and databases. These are essential skills for school and work.",
    image: noteImages.codeScreen,
    units: [
      {
        id: "2.1",
        title: "Word Processing",
        objectives: [
          "Create and format professional documents",
          "Use styles, headers, footers, and page numbers",
          "Insert tables, images, and other objects",
          "Use templates for common document types"
        ],
        keyTerms: [
          { term: "Font", definition: "The style and appearance of text characters. Includes typeface, size, and style (bold, italic)." },
          { term: "Formatting", definition: "Changing the appearance of text or document elements." },
          { term: "Header/Footer", definition: "Content that appears at the top/bottom of every page - like page numbers or document title." },
          { term: "Template", definition: "Pre-designed document format that can be reused. Saves time and ensures consistency." },
          { term: "Margins", definition: "The blank space around the edges of a page." },
          { term: "Line Spacing", definition: "The vertical space between lines of text. Single, 1.5, or double spacing." }
        ],
        studyNotes: `
## Word Processing

### What is Word Processing?
Word processing is using software to create, edit, format, and print text documents.

### Common Word Processing Software:
- Microsoft Word (most popular)
- Google Docs (free, cloud-based)
- LibreOffice Writer (free, open-source)
- Apple Pages (for Mac)

---

## Document Formatting

### Character Formatting
Applies to individual characters or words:
- **Font Family**: Arial, Times New Roman, Calibri
- **Font Size**: Measured in points (pt). 12pt is standard for body text
- **Font Style**: Bold, Italic, Underline
- **Font Color**: Black (default), or any color
- **Special**: Superscript, Subscript, Strikethrough

### Paragraph Formatting
Applies to entire paragraphs:
- **Alignment**: Left, Center, Right, Justify
- **Line Spacing**: Single (1.0), 1.5, Double (2.0)
- **Indentation**: First line, hanging, left/right indent
- **Bullets and Numbering**: For lists
- **Spacing**: Before and after paragraphs

### Page Formatting
Applies to the entire page:
- **Margins**: Top, bottom, left, right (usually 2.54cm/1 inch)
- **Orientation**: Portrait (vertical) or Landscape (horizontal)
- **Paper Size**: A4, Letter, Legal
- **Columns**: Single, two, or three columns

---

## Professional Document Features

### Headers and Footers
- Appear on every page automatically
- Good for: page numbers, document title, date, company logo
- Can be different on first page

### Table of Contents
- Auto-generated from headings
- Updates automatically when document changes
- Includes clickable page numbers

### Tables
- Organize information in rows and columns
- Can format borders, shading, alignment
- Useful for comparing data

### Images and Objects
- Insert pictures, shapes, charts
- Text wrapping: In line, Square, Tight
- Resize and position as needed
        `,
        practiceQuestions: [
          "What is the difference between character formatting and paragraph formatting?",
          "List three types of paragraph alignment and when each would be used.",
          "How do you create a header that appears on every page?",
          "What is a template? Why are templates useful?",
          "Describe how to insert and format a table in a word processor."
        ]
      },
      {
        id: "2.2",
        title: "Spreadsheets",
        objectives: [
          "Create and format spreadsheets",
          "Use formulas and functions for calculations",
          "Create charts and graphs from data",
          "Sort and filter data"
        ],
        keyTerms: [
          { term: "Cell", definition: "The intersection of a row and column. Has an address like A1, B5, C10." },
          { term: "Formula", definition: "An equation that performs calculations. Starts with = sign. Example: =A1+B1" },
          { term: "Function", definition: "Built-in formula for common calculations. Examples: SUM, AVERAGE, MAX, MIN" },
          { term: "Cell Reference", definition: "The address of a cell used in formulas. Can be relative (A1) or absolute ($A$1)." },
          { term: "Range", definition: "A group of cells. Written as start:end, like A1:A10 or B2:D5." }
        ],
        studyNotes: `
## Spreadsheets

### What is a Spreadsheet?
A spreadsheet is software for organizing, calculating, and analyzing data in rows and columns.

### Common Spreadsheet Software:
- Microsoft Excel (most popular)
- Google Sheets (free, cloud-based)
- LibreOffice Calc (free)

---

## Basic Spreadsheet Concepts

### Cell Addressing
- Columns: A, B, C, ... Z, AA, AB, ...
- Rows: 1, 2, 3, ...
- Cell address: Column + Row = A1, B5, C10

### Formulas
Always start with = sign
- Addition: =A1+B1
- Subtraction: =A1-B1
- Multiplication: =A1*B1
- Division: =A1/B1

### Common Functions
- **SUM(range)**: Add all numbers in range
  - =SUM(A1:A10) adds cells A1 through A10
  
- **AVERAGE(range)**: Calculate mean
  - =AVERAGE(B1:B5) finds average of B1 to B5
  
- **MAX(range)**: Find highest value
  - =MAX(C1:C100) returns the largest number
  
- **MIN(range)**: Find lowest value
  - =MIN(C1:C100) returns the smallest number
  
- **COUNT(range)**: Count cells with numbers
  - =COUNT(A1:A20) counts how many cells have numbers

### Cell References
- **Relative**: A1 - Changes when formula is copied
- **Absolute**: $A$1 - Stays fixed when copied
- **Mixed**: $A1 or A$1 - Column or row fixed
        `,
        practiceQuestions: [
          "What is a cell? How is a cell addressed in a spreadsheet?",
          "Write a formula to add the values in cells A1, A2, and A3.",
          "What is the difference between a formula and a function?",
          "Explain the difference between relative and absolute cell references.",
          "What function would you use to find the highest score in a list?"
        ]
      }
    ]
  },
  strand3: {
    title: "Graphics Manipulation",
    overview: "This strand covers creating and editing digital graphics and images using software tools.",
    image: noteImages.codeScreen,
    units: [
      {
        id: "3.1",
        title: "Introduction to Digital Graphics",
        objectives: [
          "Understand the difference between raster and vector graphics",
          "Use image editing software",
          "Resize, crop, and enhance images",
          "Understand file formats and compression"
        ],
        keyTerms: [
          { term: "Pixel", definition: "Picture element - the smallest unit of a digital image. Images are made of thousands of pixels." },
          { term: "Resolution", definition: "The number of pixels in an image, usually expressed as width × height (e.g., 1920×1080)." },
          { term: "Raster Graphics", definition: "Images made of pixels. Quality degrades when enlarged. Examples: JPG, PNG, GIF." },
          { term: "Vector Graphics", definition: "Images made of mathematical paths. Can be scaled without quality loss. Examples: SVG, AI." },
          { term: "DPI", definition: "Dots Per Inch - measure of print quality. Higher DPI = better print quality." },
          { term: "Compression", definition: "Reducing file size. Can be lossy (loses quality) or lossless (no quality loss)." }
        ],
        studyNotes: `
## Digital Graphics

### Types of Digital Graphics

#### Raster (Bitmap) Graphics
- Made of pixels (tiny colored squares)
- Resolution-dependent
- Quality decreases when enlarged
- Best for: photos, complex images
- Software: Photoshop, GIMP, Paint

**Common Raster Formats:**
| Format | Features |
|--------|----------|
| JPEG | Lossy compression, small files, photos |
| PNG | Lossless, transparency support |
| GIF | Animation, limited colors (256) |
| BMP | Uncompressed, large files |

#### Vector Graphics
- Made of mathematical paths and shapes
- Resolution-independent
- Can scale infinitely without quality loss
- Best for: logos, icons, illustrations
- Software: Illustrator, Inkscape, CorelDRAW

**Common Vector Formats:**
- SVG (Scalable Vector Graphics)
- AI (Adobe Illustrator)
- EPS (Encapsulated PostScript)

---

## Image Editing Basics

### Common Operations:
1. **Crop** - Remove unwanted parts of image
2. **Resize** - Change dimensions
3. **Rotate** - Turn image
4. **Adjust Brightness/Contrast** - Fix lighting
5. **Apply Filters** - Add effects

### Color Modes:
- **RGB** - Red, Green, Blue (for screens)
- **CMYK** - Cyan, Magenta, Yellow, Black (for print)
- **Grayscale** - Shades of gray only
        `,
        practiceQuestions: [
          "What is the difference between raster and vector graphics?",
          "What is a pixel? How does pixel count relate to image quality?",
          "When would you use JPEG format versus PNG format?",
          "What is the advantage of vector graphics over raster graphics?",
          "Define resolution and DPI. Why are they important for printing?"
        ]
      }
    ]
  },
  strand4: {
    title: "Communication and Network Systems",
    overview: "This strand covers understanding networks, the internet, digital communication, and online safety.",
    image: noteImages.networkCables,
    units: [
      {
        id: "4.1",
        title: "Introduction to Networks",
        image: noteImages.ethernetCable,
        objectives: [
          "Understand what a computer network is",
          "Identify different types of networks",
          "Know basic network components",
          "Understand the benefits of networking"
        ],
        keyTerms: [
          { term: "Network", definition: "Two or more computers connected together to share resources and communicate." },
          { term: "LAN", definition: "Local Area Network - network in a small area like a home, office, or school building." },
          { term: "WAN", definition: "Wide Area Network - network covering large geographic areas. The internet is a WAN." },
          { term: "Router", definition: "Device that connects networks and directs traffic between them." },
          { term: "Switch", definition: "Device that connects multiple devices on a LAN and directs data within the network." },
          { term: "IP Address", definition: "Unique numerical address for each device on a network. Like a phone number for computers." },
          { term: "Bandwidth", definition: "The amount of data that can be transmitted in a given time. Measured in Mbps or Gbps." },
          { term: "Protocol", definition: "Rules for how data is transmitted on a network. Examples: TCP/IP, HTTP, FTP." }
        ],
        studyNotes: `
## Computer Networks

### What is a Network?
A network is two or more computers connected together to:
- Share files and data
- Share hardware (printers, scanners)
- Communicate (email, chat, video calls)
- Access shared internet connection

---

## Types of Networks

### By Geographic Size:

#### PAN (Personal Area Network)
- Very small range (few meters)
- Example: Bluetooth connection between phone and headphones

#### LAN (Local Area Network)
- Single building or campus
- High speed, low latency
- Example: School network, office network

#### MAN (Metropolitan Area Network)
- City-wide coverage
- Connects multiple LANs
- Example: City government network

#### WAN (Wide Area Network)
- Large geographic area
- Country or worldwide
- Example: The Internet, corporate networks

### By Architecture:

#### Client-Server Network
- Central server provides resources
- Clients request from server
- More secure and manageable
- Used in businesses and schools

#### Peer-to-Peer (P2P) Network
- All computers equal
- Share directly with each other
- Simple and cheap
- Used in small networks, file sharing

---

## Network Components

### Hardware:
1. **Network Interface Card (NIC)** - Allows computer to connect to network
2. **Switch** - Connects devices within a LAN
3. **Router** - Connects different networks, directs traffic
4. **Modem** - Connects to internet service provider
5. **Cables** - Ethernet (wired), Fiber optic
6. **Wireless Access Point** - Provides WiFi

### Software:
- Network operating system
- Network protocols (TCP/IP)
- Firewall software
- Network monitoring tools

---

## Benefits of Networking

1. **Resource Sharing**
   - Share printers, scanners, files
   - Cost savings

2. **Communication**
   - Email, instant messaging
   - Video conferencing

3. **Data Centralization**
   - Central storage and backup
   - Easier management

4. **Internet Access**
   - Share single connection
   - Access web resources

5. **Collaboration**
   - Work on shared documents
   - Team projects
        `,
        practiceQuestions: [
          "What is a computer network? List three benefits of networking.",
          "Explain the difference between LAN and WAN with examples.",
          "What is the function of a router? How is it different from a switch?",
          "Describe the difference between client-server and peer-to-peer networks.",
          "What is an IP address? Why does every network device need one?",
          "Name four hardware components needed to create a network.",
          "What is bandwidth? Why is it important for network performance?",
          "Explain what a network protocol is and give two examples."
        ]
      },
      {
        id: "4.2",
        title: "Internet and Online Safety",
        image: noteImages.wifiNetwork,
        objectives: [
          "Understand how the internet works",
          "Know common internet services",
          "Practice safe online behavior",
          "Understand cybersecurity basics"
        ],
        keyTerms: [
          { term: "Internet", definition: "Global network of interconnected computer networks using TCP/IP protocols." },
          { term: "World Wide Web", definition: "System of websites and pages accessed via the internet using browsers." },
          { term: "URL", definition: "Uniform Resource Locator - the address of a web page. Example: https://www.example.com" },
          { term: "Browser", definition: "Software to access the web. Examples: Chrome, Firefox, Safari, Edge." },
          { term: "Search Engine", definition: "Website that helps find information. Examples: Google, Bing, Yahoo." },
          { term: "Phishing", definition: "Fake emails or websites trying to steal personal information." },
          { term: "Malware", definition: "Malicious software: viruses, worms, trojans, ransomware." },
          { term: "Firewall", definition: "Security system that monitors and controls network traffic." }
        ],
        studyNotes: `
## The Internet

### How the Internet Works
1. Your device connects to ISP (Internet Service Provider)
2. ISP connects to larger networks
3. Data travels in packets through routers
4. TCP/IP protocols manage the communication
5. DNS (Domain Name System) translates names to IP addresses

### Internet Services:
- **Web (WWW)** - Websites, web applications
- **Email** - Electronic mail
- **File Transfer (FTP)** - Upload/download files
- **Streaming** - Video and music
- **Social Media** - Communication platforms
- **Cloud Storage** - Online file storage

---

## Online Safety

### Cybersecurity Threats:

#### Malware Types:
- **Virus** - Attaches to files, spreads when shared
- **Worm** - Self-replicating, spreads via network
- **Trojan** - Disguised as legitimate software
- **Ransomware** - Encrypts files, demands payment
- **Spyware** - Secretly collects information

#### Social Engineering:
- **Phishing** - Fake emails requesting personal info
- **Scams** - Fraudulent schemes for money
- **Identity Theft** - Stealing personal information

---

## Staying Safe Online

### Password Best Practices:
- Use long passwords (12+ characters)
- Mix letters, numbers, symbols
- Don't use personal information
- Use different passwords for each account
- Consider a password manager

### Safe Browsing:
- Look for HTTPS (secure) in URL
- Don't click suspicious links
- Verify website authenticity
- Keep browser updated

### Social Media Safety:
- Use privacy settings
- Don't share personal information
- Be careful who you friend/follow
- Think before you post

### General Tips:
- Keep software updated
- Use antivirus software
- Don't download from unknown sources
- Back up important files
- Be skeptical of offers that seem too good
        `,
        practiceQuestions: [
          "What is the difference between the Internet and the World Wide Web?",
          "Name five common internet services and describe what each does.",
          "What is malware? Describe three types of malware and their effects.",
          "What is phishing? How can you protect yourself from phishing attacks?",
          "List five rules for creating strong passwords.",
          "What does HTTPS mean and why is it important?",
          "What is a firewall and how does it protect your computer?",
          "Describe three ways to stay safe on social media."
        ]
      }
    ]
  }
};

// ============================================
// YEAR 12 ICT STUDENT NOTES (EXPANDED)
// ============================================
export const year12ICTStudentNotes = {
  strand1: {
    title: "Personal Computer Systems",
    overview: "Advanced understanding of PC hardware, assembly, and maintenance. Year 12 builds on Year 11 concepts with more technical depth.",
    image: noteImages.motherboard,
    units: [
      {
        id: "1",
        title: "Advanced PC Hardware Components",
        image: noteImages.cpuSocket,
        objectives: [
          "Identify and describe all PC hardware components in detail",
          "Understand component specifications and compatibility",
          "Learn to read hardware specifications",
          "Troubleshoot common hardware issues"
        ],
        keyTerms: [
          { term: "Motherboard", definition: "Main circuit board connecting all components. Contains CPU socket, RAM slots, chipset, and expansion slots." },
          { term: "Chipset", definition: "Set of chips on motherboard that manages data flow between CPU, memory, and peripherals." },
          { term: "BIOS/UEFI", definition: "Firmware that initializes hardware during startup. UEFI is modern replacement for BIOS." },
          { term: "PCIe", definition: "Peripheral Component Interconnect Express - high-speed expansion slot for GPUs and other cards." },
          { term: "SATA", definition: "Serial ATA - interface for connecting storage devices (HDDs, SSDs, optical drives)." },
          { term: "NVMe", definition: "Non-Volatile Memory Express - faster protocol for SSDs using PCIe interface." },
          { term: "DDR RAM", definition: "Double Data Rate memory. DDR4 and DDR5 are current standards." },
          { term: "TDP", definition: "Thermal Design Power - amount of heat a component generates, measured in watts." },
          { term: "Form Factor", definition: "Physical size and layout standard. Examples: ATX, Micro-ATX, Mini-ITX for motherboards." },
          { term: "PSU", definition: "Power Supply Unit - converts AC power to DC for computer components." }
        ],
        studyNotes: `
## Advanced PC Hardware

### Motherboard Architecture

The motherboard is the backbone of any computer. Understanding its components is crucial for building and troubleshooting PCs.

#### Key Motherboard Components:

1. **CPU Socket**
   - Physical slot where CPU is installed
   - Must match CPU type (Intel LGA or AMD AM)
   - Examples: LGA 1700 (Intel 12th/13th gen), AM5 (AMD Ryzen 7000)

2. **Chipset**
   - Manages communication between components
   - North Bridge: CPU, RAM, PCIe (now often in CPU)
   - South Bridge: USB, SATA, audio, networking

3. **RAM Slots (DIMM)**
   - Typically 2-4 slots
   - Must match DDR type (DDR4 or DDR5)
   - Dual-channel configuration for better performance

4. **Expansion Slots**
   - PCIe x16: For graphics cards
   - PCIe x4/x1: For sound cards, network cards, etc.
   - M.2 slots: For NVMe SSDs

5. **Storage Connectors**
   - SATA ports: For HDDs, SSDs, optical drives
   - M.2 slots: For NVMe or SATA M.2 drives

6. **Power Connectors**
   - 24-pin ATX main power
   - 8-pin CPU power (4+4 pin)
   - PCIe power for graphics

7. **I/O Panel**
   - USB ports, audio jacks
   - Network port (Ethernet)
   - Display outputs (if using integrated graphics)

---

### CPU Specifications Explained

When choosing or understanding a CPU, these specs matter:

| Specification | Meaning | Example |
|--------------|---------|---------|
| Cores | Processing units | 8 cores |
| Threads | Virtual processing units | 16 threads |
| Base Clock | Default operating speed | 3.5 GHz |
| Boost Clock | Maximum speed under load | 5.0 GHz |
| Cache | Fast memory on CPU | 32 MB L3 |
| TDP | Heat output | 125W |
| Socket | Physical connector type | LGA 1700 |

### RAM Specifications

| Specification | Meaning |
|--------------|---------|
| Capacity | Amount of memory (8GB, 16GB, 32GB) |
| Speed | Data rate in MHz (3200MHz, 6000MHz) |
| Type | DDR4 or DDR5 |
| Latency | CAS Latency (CL16, CL36) - lower is faster |
| Channels | Dual-channel for better bandwidth |

---

### Storage Technology

#### SATA SSD vs NVMe SSD

| Feature | SATA SSD | NVMe SSD |
|---------|----------|----------|
| Interface | SATA III (6 Gbps) | PCIe (varies) |
| Max Speed | ~550 MB/s | 3,500-7,000+ MB/s |
| Form Factor | 2.5" or M.2 | M.2 only |
| Price | Lower | Higher |
| Best For | Budget builds | High performance |

---

### Component Compatibility

When building a PC, ensure compatibility:

1. **CPU + Motherboard**
   - Socket must match
   - Chipset should support CPU

2. **RAM + Motherboard**
   - DDR type must match (DDR4 or DDR5)
   - Speed supported by motherboard

3. **GPU + PSU**
   - PSU must provide enough wattage
   - Correct power connectors available

4. **Components + Case**
   - Motherboard form factor fits case
   - GPU length fits case
   - Cooling clearance

5. **PSU Power Calculation**
   - CPU TDP + GPU TDP + ~100W overhead
   - Use online PSU calculators

---

### Troubleshooting Hardware Issues

**Computer Won't Turn On:**
- Check power cable connection
- Test power outlet
- Check PSU switch
- Test PSU with paperclip test

**No Display:**
- Check monitor cable and input
- Verify GPU is seated properly
- Try integrated graphics
- Check RAM is properly installed

**Random Crashes/Blue Screen:**
- Check temperatures (overheating)
- Test RAM with MemTest86
- Update drivers
- Check for failing storage

**Strange Noises:**
- Clicking from HDD = failing drive, backup immediately
- Fan noise = clean dust or replace fan
- Coil whine from GPU = normal for some cards
        `,
        practiceQuestions: [
          "Name five major components found on a motherboard and describe the function of each.",
          "What factors determine CPU performance? Explain cores, threads, and clock speed.",
          "Compare SATA SSD and NVMe SSD in terms of speed, interface, and use cases.",
          "What compatibility factors must you consider when building a PC?",
          "Explain what TDP means and why it's important when choosing a CPU cooler.",
          "What is the difference between DDR4 and DDR5 RAM?",
          "Describe the troubleshooting steps if a newly built PC won't turn on.",
          "What is a chipset? Why is it important to choose the right one?"
        ]
      },
      {
        id: "2",
        title: "Building and Assembling a PC",
        objectives: [
          "Understand the steps to assemble a computer",
          "Install hardware components properly",
          "Configure BIOS/UEFI settings",
          "Install operating system"
        ],
        keyTerms: [
          { term: "Anti-static", definition: "Precautions to prevent static electricity damage to components. Use wrist strap and mat." },
          { term: "Standoffs", definition: "Metal spacers that hold motherboard above case to prevent short circuits." },
          { term: "Thermal Paste", definition: "Heat-conducting compound between CPU and cooler for efficient heat transfer." },
          { term: "Cable Management", definition: "Organizing cables neatly for airflow and aesthetics." },
          { term: "POST", definition: "Power-On Self-Test - diagnostic test run when computer starts." },
          { term: "Boot Device", definition: "Storage device containing operating system that computer starts from." }
        ],
        studyNotes: `
## PC Assembly Guide

### Before You Start

**Tools Needed:**
- Phillips head screwdriver
- Anti-static wrist strap
- Flashlight/headlamp
- Cable ties

**Safety Precautions:**
- Work on non-carpeted surface
- Use anti-static wrist strap
- Don't force components
- Handle components by edges
- Keep work area organized

---

### Assembly Steps

#### Step 1: Prepare the Case
1. Remove side panels
2. Install standoffs for motherboard
3. Install I/O shield (comes with motherboard)
4. Plan cable routing

#### Step 2: Install CPU
1. Open CPU socket lever
2. Align CPU with socket (look for triangle marker)
3. Gently place CPU - do NOT push
4. Close lever to secure

#### Step 3: Install RAM
1. Open RAM slot clips
2. Align RAM notch with slot
3. Press firmly until clips click
4. For dual-channel: use slots 2 and 4 (usually)

#### Step 4: Install CPU Cooler
1. Apply thermal paste (pea-sized dot)
2. Mount cooler on CPU
3. Secure with brackets/screws
4. Connect fan to CPU_FAN header

#### Step 5: Install Motherboard
1. Align with I/O shield and standoffs
2. Insert at angle, then lay flat
3. Secure with screws (don't overtighten)
4. Start with corners, then middle

#### Step 6: Install Storage
1. SATA drives: Mount in drive bay, connect SATA and power
2. M.2 NVMe: Insert at angle, secure with screw

#### Step 7: Install Graphics Card
1. Remove expansion slot covers
2. Insert GPU into PCIe x16 slot
3. Press until click
4. Secure with bracket screw
5. Connect power cables

#### Step 8: Connect Power Supply
1. 24-pin ATX to motherboard
2. 8-pin CPU power
3. SATA power to drives
4. PCIe power to GPU
5. Fan and case connectors

#### Step 9: First Boot
1. Double-check all connections
2. Connect monitor, keyboard, mouse
3. Power on
4. Enter BIOS/UEFI (usually DEL or F2)
5. Check all components detected
6. Install operating system

---

### BIOS/UEFI Configuration

**Common Settings:**
- Boot order: Set SSD/HDD as first boot device
- Enable XMP/EXPO for RAM speed
- Fan curves for cooling
- Secure Boot for Windows 11
        `,
        practiceQuestions: [
          "List the steps to install a CPU in order.",
          "Why is thermal paste important? What happens without it?",
          "What safety precautions should you take when building a PC?",
          "How do you know if RAM is installed correctly?",
          "What is POST? What does it indicate when you hear a single beep?",
          "Describe how to properly install a graphics card."
        ]
      }
    ]
  },
  strand2: {
    title: "Application Software",
    overview: "Advanced productivity software including databases and web development.",
    image: noteImages.codeScreen,
    units: [
      {
        id: "1",
        title: "Database Fundamentals",
        objectives: [
          "Understand database concepts",
          "Create tables and relationships",
          "Write basic SQL queries",
          "Design a simple database"
        ],
        keyTerms: [
          { term: "Database", definition: "Organized collection of structured data, typically stored electronically." },
          { term: "Table", definition: "Collection of related data organized in rows and columns." },
          { term: "Record", definition: "A single row in a table, containing data about one item." },
          { term: "Field", definition: "A column in a table, representing one attribute." },
          { term: "Primary Key", definition: "Unique identifier for each record in a table." },
          { term: "Foreign Key", definition: "Field that links to primary key of another table." },
          { term: "SQL", definition: "Structured Query Language - language for managing databases." },
          { term: "Query", definition: "Request to retrieve or manipulate data from a database." }
        ],
        studyNotes: `
## Database Fundamentals

### What is a Database?
A database is an organized collection of data stored electronically. Databases make it easy to store, retrieve, and manage large amounts of information.

**Examples of Database Use:**
- School student records
- Library book catalog
- Online shopping inventory
- Bank account information
- Social media posts

---

### Database Concepts

#### Tables
Tables organize data into rows and columns:
- **Rows (Records)**: Each row represents one item
- **Columns (Fields)**: Each column represents an attribute

**Example: Student Table**
| StudentID | FirstName | LastName | Grade | DateOfBirth |
|-----------|-----------|----------|-------|-------------|
| 001 | John | Smith | 11 | 2009-03-15 |
| 002 | Mary | Johnson | 12 | 2008-07-22 |
| 003 | David | Brown | 11 | 2009-01-10 |

#### Keys
- **Primary Key**: Unique identifier (StudentID)
- **Foreign Key**: Links to another table's primary key

#### Relationships
- **One-to-One**: One record matches one record
- **One-to-Many**: One record matches many records
- **Many-to-Many**: Many records match many records

---

### Basic SQL

#### SELECT - Retrieve data
\`\`\`sql
SELECT * FROM Students;
SELECT FirstName, LastName FROM Students;
SELECT * FROM Students WHERE Grade = 11;
\`\`\`

#### INSERT - Add data
\`\`\`sql
INSERT INTO Students (StudentID, FirstName, LastName, Grade)
VALUES (004, 'Sarah', 'Wilson', 12);
\`\`\`

#### UPDATE - Modify data
\`\`\`sql
UPDATE Students SET Grade = 12 WHERE StudentID = 001;
\`\`\`

#### DELETE - Remove data
\`\`\`sql
DELETE FROM Students WHERE StudentID = 004;
\`\`\`
        `,
        practiceQuestions: [
          "What is a database? Give three examples of database use.",
          "Explain the difference between a table, record, and field.",
          "What is a primary key? Why is it important?",
          "Write a SQL query to select all students in Grade 12.",
          "What is the purpose of a foreign key?"
        ]
      }
    ]
  },
  strand3: {
    title: "Graphics and Programming",
    overview: "Digital graphics creation and advanced programming concepts.",
    image: noteImages.programming,
    units: []
  },
  strand4: {
    title: "Networks and ICT Issues",
    overview: "Network infrastructure, security, and ethical issues in ICT.",
    image: noteImages.serverRoom,
    units: []
  }
};

// ============================================
// YEAR 13 ICT STUDENT NOTES (EXPANDED)
// ============================================
export const year13ICTStudentNotes = {
  strand1: {
    title: "Personal Computer Systems",
    overview: "Advanced topics including future ICT trends, information systems management, and ICT infrastructure. Prepare for national examinations with comprehensive study materials.",
    image: noteImages.serverRoom,
    units: [
      {
        id: "1",
        title: "Future ICT Trends",
        image: noteImages.wifiNetwork,
        objectives: [
          "Identify emerging ICT trends in education, health, and home sectors",
          "Analyse positive and negative impacts of future ICT developments",
          "Understand how Vanuatu can prepare for ICT advancement",
          "Evaluate the role of AI, IoT, and cloud computing"
        ],
        keyTerms: [
          { term: "Artificial Intelligence (AI)", definition: "Computer systems that can perform tasks requiring human intelligence, such as learning, reasoning, and problem-solving." },
          { term: "Machine Learning", definition: "Subset of AI where systems learn and improve from experience without being explicitly programmed." },
          { term: "Internet of Things (IoT)", definition: "Network of physical devices connected to the internet, collecting and sharing data. Examples: smart home devices, wearables." },
          { term: "Cloud Computing", definition: "Delivery of computing services (servers, storage, databases, software) over the internet." },
          { term: "Big Data", definition: "Extremely large data sets that require special tools to store, process, and analyze for patterns and trends." },
          { term: "Blockchain", definition: "Decentralized, distributed digital ledger technology. Used in cryptocurrency and secure record-keeping." },
          { term: "5G", definition: "Fifth generation mobile network. Much faster than 4G, enables new technologies like self-driving cars." },
          { term: "Virtual Reality (VR)", definition: "Computer-generated simulation of a 3D environment that can be interacted with using special equipment." },
          { term: "Augmented Reality (AR)", definition: "Technology that overlays digital information on the real world through a device like a smartphone." },
          { term: "Quantum Computing", definition: "Computing using quantum mechanics principles. Potentially much faster than traditional computers for certain tasks." }
        ],
        studyNotes: `
## Future ICT Trends

### Introduction
Information and Communication Technology is evolving rapidly. Understanding these trends is crucial for preparing Vanuatu for the future.

---

## Major Emerging Technologies

### 1. Artificial Intelligence (AI)

AI enables machines to think and learn like humans.

**Types of AI:**
- **Narrow AI**: Designed for specific tasks (Siri, Google Assistant)
- **General AI**: Human-level intelligence (not yet achieved)
- **Super AI**: Beyond human intelligence (theoretical)

**AI Applications:**
- Voice assistants (Siri, Alexa, Google Assistant)
- Recommendation systems (Netflix, YouTube)
- Medical diagnosis
- Self-driving vehicles
- Language translation

**Machine Learning:**
- Subset of AI
- Systems learn from data
- Improve over time without programming
- Examples: spam filters, fraud detection

### 2. Internet of Things (IoT)

IoT connects everyday objects to the internet.

**Smart Home Devices:**
- Smart thermostats
- Security cameras
- Smart lights
- Voice-controlled assistants
- Smart refrigerators

**IoT in Other Sectors:**
- Agriculture: Soil sensors, automated irrigation
- Healthcare: Wearable health monitors
- Transportation: Connected vehicles, traffic management
- Industry: Factory automation, predictive maintenance

**IoT Challenges:**
- Security vulnerabilities
- Privacy concerns
- Interoperability issues
- Power consumption

### 3. Cloud Computing

Delivering computing services over the internet.

**Service Models:**

| Model | Description | Examples |
|-------|-------------|----------|
| SaaS | Software as a Service - use applications online | Google Docs, Office 365, Salesforce |
| PaaS | Platform as a Service - develop applications online | Google App Engine, Heroku |
| IaaS | Infrastructure as a Service - rent computing resources | AWS, Azure, Google Cloud |

**Benefits:**
- Cost savings (no hardware to buy)
- Scalability (grow/shrink as needed)
- Accessibility (access from anywhere)
- Automatic updates

**Challenges:**
- Internet dependency
- Security concerns
- Vendor lock-in

### 4. Big Data

Massive volumes of data requiring special processing.

**The 5 V's of Big Data:**
1. **Volume**: Huge amounts of data
2. **Velocity**: Fast generation and processing
3. **Variety**: Different types (text, images, video)
4. **Veracity**: Data accuracy and quality
5. **Value**: Useful insights from data

**Big Data Applications:**
- Business analytics
- Healthcare research
- Weather prediction
- Social media analysis
- Fraud detection

---

## ICT in Different Sectors

### ICT in Education

**Current Technologies:**
- Online learning platforms (Google Classroom, Moodle)
- Video conferencing (Zoom, Teams)
- Educational apps and games
- Digital textbooks

**Future Trends:**
- AI-powered personalized learning
- Virtual reality classrooms
- Automated assessment
- Adaptive learning systems

**Benefits for Vanuatu:**
- Access to quality education in remote areas
- Teacher training resources
- Student collaboration across islands

### ICT in Healthcare

**Current Technologies:**
- Electronic health records
- Telemedicine consultations
- Medical imaging
- Health tracking apps

**Future Trends:**
- AI diagnosis assistance
- Remote patient monitoring
- Robot-assisted surgery
- Personalized medicine using genetics

**Benefits for Vanuatu:**
- Healthcare access in remote areas
- Specialist consultations via video
- Improved health record management
- Disease outbreak tracking

### ICT in Home

**Smart Home Features:**
- Voice-controlled devices
- Automated lighting and climate
- Security systems
- Energy management

**Future Trends:**
- Fully integrated smart homes
- AI home assistants
- Predictive maintenance
- Energy optimization

---

## Vanuatu and ICT

### Current Challenges:
- Limited internet infrastructure
- High cost of internet access
- Digital literacy gaps
- Frequent natural disasters affecting infrastructure

### Preparation Strategies:
1. **Infrastructure Development**
   - Expand broadband coverage
   - Improve mobile network
   - Undersea cable connections

2. **Digital Literacy**
   - ICT education in schools
   - Teacher training programs
   - Community digital centers

3. **E-Government Services**
   - Online government services
   - Digital identity systems
   - Electronic payments

4. **Economic Opportunities**
   - Remote work possibilities
   - Tech startups
   - Digital tourism marketing

### Positive Impacts of ICT:
- Improved communication
- Access to information
- Economic opportunities
- Better education access
- Enhanced healthcare

### Negative Impacts:
- Digital divide
- Job displacement
- Privacy concerns
- Cybersecurity threats
- Technology dependency
        `,
        practiceQuestions: [
          "What is Artificial Intelligence? Describe three applications of AI in everyday life.",
          "Explain the Internet of Things (IoT) and give four examples of IoT devices.",
          "Compare SaaS, PaaS, and IaaS cloud computing models with examples.",
          "What are the '5 V's' of Big Data? Explain each one.",
          "How can ICT improve education in Vanuatu? Give three specific examples.",
          "Discuss the positive and negative impacts of future ICT trends on society.",
          "What challenges does Vanuatu face in ICT development? Suggest solutions.",
          "How can telemedicine help healthcare in remote areas of Vanuatu?",
          "What is the difference between Virtual Reality (VR) and Augmented Reality (AR)?",
          "Why is digital literacy important for Vanuatu's future? How can it be improved?"
        ],
        examTips: [
          "Be able to discuss both benefits AND challenges of new technologies",
          "Use specific examples from Vanuatu context when possible",
          "Understand the practical applications of AI, IoT, and cloud computing",
          "Know the differences between service models (SaaS, PaaS, IaaS)"
        ]
      },
      {
        id: "2",
        title: "Management of Information Systems",
        objectives: [
          "Understand information system workflows",
          "Learn IS development lifecycle (SDLC)",
          "Evaluate IS benefits in organizations",
          "Design basic information systems"
        ],
        keyTerms: [
          { term: "Information System (IS)", definition: "Organized system for collecting, organizing, storing, and communicating information within an organization." },
          { term: "SDLC", definition: "Systems Development Life Cycle - structured methodology for developing information systems." },
          { term: "Feasibility Study", definition: "Analysis to determine if a proposed system is practical and worthwhile." },
          { term: "Requirements Analysis", definition: "Process of identifying what stakeholders need from a system." },
          { term: "Data Flow Diagram (DFD)", definition: "Visual representation of how data moves through a system." },
          { term: "Entity Relationship Diagram (ERD)", definition: "Visual model of database entities and their relationships." },
          { term: "ERP", definition: "Enterprise Resource Planning - integrated business management software." },
          { term: "CRM", definition: "Customer Relationship Management - system for managing customer interactions." }
        ],
        studyNotes: `
## Information Systems Management

### What is an Information System?
An Information System (IS) is an organized system for:
- Collecting data
- Processing information
- Storing records
- Distributing information
- Supporting decision-making

**Components of an IS:**
1. Hardware - computers, servers, devices
2. Software - applications, operating systems
3. Data - information stored in system
4. People - users, administrators, developers
5. Procedures - rules and processes

---

## Types of Information Systems

### Transaction Processing Systems (TPS)
- Process daily business transactions
- Examples: Point of sale, payroll, banking
- High volume, routine transactions
- Accuracy is critical

### Management Information Systems (MIS)
- Generate reports for managers
- Use data from TPS
- Support operational decisions
- Examples: Sales reports, inventory reports

### Decision Support Systems (DSS)
- Support complex decision-making
- "What-if" analysis
- Interactive and flexible
- Examples: Financial planning tools

### Executive Information Systems (EIS)
- High-level summaries for executives
- Strategic planning support
- Easy-to-use dashboards
- Examples: Company performance dashboards

---

## Systems Development Life Cycle (SDLC)

### Phase 1: Planning
- Identify the problem or opportunity
- Conduct feasibility study
- Define project scope
- Create project plan

**Feasibility Study Areas:**
- Technical: Can we build it?
- Economic: Is it worth the cost?
- Operational: Will people use it?
- Schedule: Can we finish on time?

### Phase 2: Analysis
- Gather requirements from users
- Document current system
- Create system specifications
- Create data flow diagrams

**Techniques:**
- Interviews with users
- Surveys and questionnaires
- Observation of current system
- Document review

### Phase 3: Design
- Create system architecture
- Design database structure
- Design user interface
- Specify hardware/software

**Outputs:**
- System design document
- Database schema (ERD)
- Interface mockups
- Technical specifications

### Phase 4: Implementation
- Write program code
- Build database
- Test individual components
- Integrate components

### Phase 5: Testing
- Unit testing (individual parts)
- Integration testing (combined parts)
- System testing (whole system)
- User acceptance testing (UAT)

### Phase 6: Deployment
- Install system
- Migrate data
- Train users
- Switch over from old system

**Deployment Strategies:**
- Direct cutover: Stop old, start new immediately
- Parallel: Run both systems together
- Phased: Implement in stages
- Pilot: Test with small group first

### Phase 7: Maintenance
- Fix bugs
- Make enhancements
- Provide user support
- Update documentation

---

## Benefits of Information Systems

### For Organizations:
1. **Improved Efficiency**
   - Automate routine tasks
   - Reduce manual errors
   - Faster processing

2. **Better Decision-Making**
   - Access to accurate information
   - Data analysis tools
   - Real-time reporting

3. **Cost Reduction**
   - Less paper and storage
   - Fewer staff for routine tasks
   - Better inventory management

4. **Competitive Advantage**
   - Better customer service
   - Faster response to market
   - Innovation capability

5. **Improved Communication**
   - Information sharing
   - Collaboration tools
   - Remote access
        `,
        practiceQuestions: [
          "What is an Information System? Name its five components.",
          "Describe the four types of information systems (TPS, MIS, DSS, EIS) with examples.",
          "List and explain the seven phases of the SDLC.",
          "What is a feasibility study? What four areas does it examine?",
          "Why is the requirements analysis phase important?",
          "Compare the four deployment strategies: direct cutover, parallel, phased, and pilot.",
          "List five benefits of implementing an information system in an organization.",
          "What is the purpose of user acceptance testing (UAT)?",
          "Draw a simple data flow diagram for a school enrollment system.",
          "Why is the maintenance phase never really complete?"
        ]
      }
    ]
  },
  strand2: {
    title: "Application Software",
    overview: "Advanced web development, databases, and multimedia production.",
    image: noteImages.programming,
    units: []
  },
  strand3: {
    title: "Graphics and Programming",
    overview: "Advanced programming concepts, algorithms, and digital systems.",
    image: noteImages.codeLaptop,
    units: []
  },
  strand4: {
    title: "Networks and ICT Issues",
    overview: "Advanced networking, cybersecurity, and ethical considerations.",
    image: noteImages.serverRoom,
    units: []
  }
};

// ============================================
// CHEMISTRY STUDENT NOTES (EXPANDED)
// ============================================
export const year11ChemistryStudentNotes = {
  strand1: {
    title: "Atomic Structure, Bonding and Related Properties",
    overview: "Understanding atomic structure, electron configuration, and chemical bonding. This forms the foundation for all chemistry concepts.",
    image: noteImages.atomStructure,
    units: [
      {
        id: "1",
        title: "Atomic Structure",
        image: noteImages.molecules,
        objectives: [
          "Describe the structure of an atom",
          "Define mass number, atomic number, and isotopes",
          "Write electronic configurations for the first 20 elements",
          "Understand the periodic table organization",
          "Calculate numbers of subatomic particles"
        ],
        keyTerms: [
          { term: "Atom", definition: "The smallest unit of an element that retains its chemical properties. Made of protons, neutrons, and electrons." },
          { term: "Proton", definition: "Positively charged particle in the nucleus. Mass ≈ 1 amu. Charge = +1." },
          { term: "Neutron", definition: "Neutral particle in the nucleus. Mass ≈ 1 amu. Charge = 0." },
          { term: "Electron", definition: "Negatively charged particle orbiting the nucleus. Mass ≈ 0 amu. Charge = -1." },
          { term: "Nucleus", definition: "Dense central core of atom containing protons and neutrons." },
          { term: "Atomic Number (Z)", definition: "Number of protons in an atom's nucleus. Defines the element." },
          { term: "Mass Number (A)", definition: "Total number of protons and neutrons in the nucleus." },
          { term: "Isotope", definition: "Atoms of the same element with different numbers of neutrons (same Z, different A)." },
          { term: "Electron Shell", definition: "Energy level where electrons orbit the nucleus. Also called electron orbit." },
          { term: "Valence Electrons", definition: "Electrons in the outermost shell. Determine chemical properties and bonding." },
          { term: "Ion", definition: "Atom with unequal protons and electrons, giving it a charge." },
          { term: "Relative Atomic Mass", definition: "Weighted average mass of all isotopes of an element." }
        ],
        studyNotes: `
## Atomic Structure

### The Discovery of Atomic Structure

#### Timeline of Discovery:
- **1897** - J.J. Thomson discovers electron (cathode ray experiments)
- **1909** - Ernest Rutherford's gold foil experiment discovers nucleus
- **1913** - Niels Bohr proposes electron shells/orbits
- **1932** - James Chadwick discovers neutron

#### Rutherford's Gold Foil Experiment:
- Fired alpha particles at thin gold foil
- Most particles passed through (atom is mostly empty space)
- Some deflected (positive nucleus)
- Few bounced back (dense, positive center)

---

## Parts of an Atom

### The Nucleus
Located at the center of the atom:
- Contains protons and neutrons
- Very small but very dense
- Carries positive charge (from protons)

### Subatomic Particles

| Particle | Location | Charge | Mass (amu) |
|----------|----------|--------|------------|
| Proton | Nucleus | +1 | 1 |
| Neutron | Nucleus | 0 | 1 |
| Electron | Orbitals | -1 | ~0 (1/1836) |

### Key Numbers

**Atomic Number (Z)**
- Number of protons
- Defines the element
- On periodic table
- In a neutral atom: protons = electrons

**Mass Number (A)**
- Protons + Neutrons
- Written as superscript (¹²C)
- Can vary (isotopes)

**Calculating Particles:**
\`\`\`
Number of protons = Atomic number
Number of electrons = Atomic number (in neutral atom)
Number of neutrons = Mass number - Atomic number
\`\`\`

**Example: Carbon-12 (¹²₆C)**
- Atomic number = 6 → 6 protons, 6 electrons
- Mass number = 12 → 12 - 6 = 6 neutrons

---

## Isotopes

Isotopes are atoms of the **same element** with **different mass numbers**.
- Same number of protons (same element)
- Different number of neutrons
- Similar chemical properties
- Different physical properties

### Common Isotopes:

**Carbon Isotopes:**
- Carbon-12: 6 protons, 6 neutrons (99%)
- Carbon-13: 6 protons, 7 neutrons (1%)
- Carbon-14: 6 protons, 8 neutrons (trace) - radioactive, used for dating

**Hydrogen Isotopes:**
- Protium (¹H): 0 neutrons - most common
- Deuterium (²H): 1 neutron - "heavy hydrogen"
- Tritium (³H): 2 neutrons - radioactive

### Relative Atomic Mass
Weighted average of all isotopes based on natural abundance.

**Example: Chlorine**
- Cl-35 (75%) + Cl-37 (25%)
- Relative atomic mass = (35 × 0.75) + (37 × 0.25) = 35.5

---

## Electronic Configuration

### Electron Shells
Electrons occupy energy levels (shells) around the nucleus.

| Shell | Name | Maximum Electrons |
|-------|------|-------------------|
| 1 | K | 2 |
| 2 | L | 8 |
| 3 | M | 8 (18 for heavier elements) |
| 4 | N | 8 (32 for heavier elements) |

**Rule:** 2n² electrons per shell (where n = shell number)

### Writing Electronic Configurations

**Rules:**
1. Fill lowest energy shells first
2. Don't exceed maximum for each shell
3. Write as comma-separated numbers

**First 20 Elements:**
| Element | Symbol | Z | Configuration |
|---------|--------|---|---------------|
| Hydrogen | H | 1 | 1 |
| Helium | He | 2 | 2 |
| Lithium | Li | 3 | 2, 1 |
| Beryllium | Be | 4 | 2, 2 |
| Boron | B | 5 | 2, 3 |
| Carbon | C | 6 | 2, 4 |
| Nitrogen | N | 7 | 2, 5 |
| Oxygen | O | 8 | 2, 6 |
| Fluorine | F | 9 | 2, 7 |
| Neon | Ne | 10 | 2, 8 |
| Sodium | Na | 11 | 2, 8, 1 |
| Magnesium | Mg | 12 | 2, 8, 2 |
| Aluminium | Al | 13 | 2, 8, 3 |
| Silicon | Si | 14 | 2, 8, 4 |
| Phosphorus | P | 15 | 2, 8, 5 |
| Sulfur | S | 16 | 2, 8, 6 |
| Chlorine | Cl | 17 | 2, 8, 7 |
| Argon | Ar | 18 | 2, 8, 8 |
| Potassium | K | 19 | 2, 8, 8, 1 |
| Calcium | Ca | 20 | 2, 8, 8, 2 |

---

## The Periodic Table

### Organization:
- **Periods** (rows): Elements in same period have same number of shells
- **Groups** (columns): Elements in same group have same number of valence electrons

### Groups:
- Group 1: Alkali metals (1 valence electron)
- Group 2: Alkaline earth metals (2 valence electrons)
- Group 17: Halogens (7 valence electrons)
- Group 18: Noble gases (8 valence electrons - full outer shell)

### Periodic Trends:
- **Atomic radius**: Decreases across period, increases down group
- **Electronegativity**: Increases across period, decreases down group
- **Reactivity of metals**: Increases down group
- **Reactivity of non-metals**: Decreases down group
        `,
        practiceQuestions: [
          "Draw and label the structure of an atom, showing the nucleus and electron shells.",
          "Define atomic number and mass number. How are they different?",
          "An atom has 17 protons and 18 neutrons. What is its atomic number and mass number?",
          "What are isotopes? Give two examples of elements with common isotopes.",
          "Write the electronic configuration for: sodium (Na), chlorine (Cl), and calcium (Ca).",
          "How many protons, neutrons, and electrons are in ³⁹₁₉K?",
          "Explain what valence electrons are and why they are important.",
          "Calculate the relative atomic mass of copper if Cu-63 is 69% and Cu-65 is 31%.",
          "Describe Rutherford's gold foil experiment and its conclusions.",
          "Why do elements in the same group have similar chemical properties?"
        ],
        examTips: [
          "Memorize electronic configurations for the first 20 elements",
          "Practice calculating numbers of subatomic particles",
          "Understand the relationship between electron configuration and periodic table position",
          "Know how to calculate relative atomic mass from isotope data"
        ]
      },
      {
        id: "2",
        title: "Chemical Bonding",
        image: noteImages.molecules,
        objectives: [
          "Understand ionic and covalent bonding",
          "Draw Lewis (dot-cross) structures",
          "Predict properties based on bond type",
          "Understand metallic bonding",
          "Explain intermolecular forces"
        ],
        keyTerms: [
          { term: "Chemical Bond", definition: "Attractive force that holds atoms together in a compound." },
          { term: "Ionic Bond", definition: "Bond formed by transfer of electrons from metal to non-metal, creating ions." },
          { term: "Covalent Bond", definition: "Bond formed by sharing electrons between non-metal atoms." },
          { term: "Metallic Bond", definition: "Bond in metals where electrons are shared among all atoms." },
          { term: "Lewis Structure", definition: "Diagram showing valence electrons as dots around element symbol." },
          { term: "Electronegativity", definition: "Ability of an atom to attract electrons in a bond. Fluorine is most electronegative." },
          { term: "Cation", definition: "Positively charged ion, formed by losing electrons." },
          { term: "Anion", definition: "Negatively charged ion, formed by gaining electrons." },
          { term: "Octet Rule", definition: "Atoms tend to gain, lose, or share electrons to have 8 electrons in outer shell." },
          { term: "Molecule", definition: "Group of atoms joined by covalent bonds." },
          { term: "Lattice", definition: "Regular 3D arrangement of ions in an ionic compound." },
          { term: "Intermolecular Forces", definition: "Weak attractions between molecules (not bonds)." }
        ],
        studyNotes: `
## Chemical Bonding

### Why Do Atoms Bond?
Atoms bond to achieve a stable electron configuration - usually 8 electrons in outer shell (octet rule). This is the configuration of noble gases.

---

## Types of Chemical Bonds

### 1. Ionic Bonding

**Formation:**
- Transfer of electrons from metal to non-metal
- Metal loses electrons → positive ion (cation)
- Non-metal gains electrons → negative ion (anion)
- Opposite charges attract → ionic bond

**Example: Sodium Chloride (NaCl)**
\`\`\`
Na (2,8,1) → Na⁺ (2,8) + e⁻
Cl (2,8,7) + e⁻ → Cl⁻ (2,8,8)
Na⁺ + Cl⁻ → NaCl
\`\`\`

**Properties of Ionic Compounds:**
| Property | Explanation |
|----------|-------------|
| High melting point | Strong ionic bonds require lots of energy to break |
| Solid at room temp | Ions locked in lattice structure |
| Conducts when dissolved/molten | Ions free to move and carry charge |
| Does not conduct as solid | Ions fixed in position |
| Often soluble in water | Water molecules can separate ions |
| Brittle | Shifting layers causes like charges to repel |

### 2. Covalent Bonding

**Formation:**
- Sharing of electrons between non-metal atoms
- Each atom contributes electrons to shared pair
- Shared electrons count toward both atoms' octets

**Types of Covalent Bonds:**
- **Single bond**: 1 shared pair (e.g., H-H)
- **Double bond**: 2 shared pairs (e.g., O=O)
- **Triple bond**: 3 shared pairs (e.g., N≡N)

**Example: Water (H₂O)**
\`\`\`
      H
       \\
    O - Oxygen shares electrons with 2 hydrogen atoms
       /
      H
\`\`\`

**Properties of Covalent Compounds:**
| Property | Simple Molecular | Giant Covalent |
|----------|-----------------|----------------|
| Melting point | Low | Very high |
| Boiling point | Low | Very high |
| Conducts electricity | No | No (except graphite) |
| Solubility | Varies | Insoluble |
| Examples | H₂O, CO₂ | Diamond, silicon |

### 3. Metallic Bonding

**Formation:**
- Metal atoms lose outer electrons
- Electrons form "sea of delocalized electrons"
- Positive metal ions in regular lattice
- Electrons free to move

**Properties of Metals:**
| Property | Explanation |
|----------|-------------|
| Good conductors | Delocalized electrons carry current |
| Malleable | Layers can slide without breaking bonds |
| Ductile | Can be drawn into wires |
| Shiny | Electrons reflect light |
| High melting points | Strong metallic bonds |

---

## Drawing Lewis Structures

### Steps:
1. Count total valence electrons
2. Draw single bonds between atoms
3. Complete octets with lone pairs
4. Check electron count

### Examples:

**Water (H₂O):**
- O has 6 valence electrons
- Each H has 1 valence electron
- Total = 6 + 1 + 1 = 8 electrons

\`\`\`
        H - O - H
            : :
\`\`\`
O shares with 2 H, has 2 lone pairs

**Carbon Dioxide (CO₂):**
- C has 4 valence electrons
- Each O has 6 valence electrons
- Total = 4 + 6 + 6 = 16 electrons

\`\`\`
    : O = C = O :
\`\`\`
Double bonds between C and each O

**Ammonia (NH₃):**
\`\`\`
        H
        |
    H - N :
        |
        H
\`\`\`
N has 1 lone pair

---

## Comparing Bond Types

| Property | Ionic | Covalent | Metallic |
|----------|-------|----------|----------|
| Particles | Ions | Molecules | Metal ions + electrons |
| Bonding | Electron transfer | Electron sharing | Electron pooling |
| Melting point | High | Low (usually) | Varies (usually high) |
| Conducts (solid) | No | No | Yes |
| Conducts (liquid) | Yes | No | Yes |
| Structure | Lattice | Molecules | Lattice |
        `,
        practiceQuestions: [
          "What is the difference between ionic and covalent bonds?",
          "Draw the Lewis structure for water (H₂O) and label the bond type.",
          "Explain why ionic compounds conduct electricity when dissolved but not as solids.",
          "Why do metals conduct electricity? Describe metallic bonding.",
          "Predict the properties of MgO (ionic) versus CO₂ (covalent).",
          "Draw Lewis structures for: CH₄, NH₃, and NaCl.",
          "What is electronegativity? How does it affect bond type?",
          "Explain the octet rule. Why do atoms follow this rule?",
          "Compare the melting points of diamond, sodium chloride, and water. Explain the differences.",
          "Why is water a liquid at room temperature while methane is a gas?"
        ],
        examTips: [
          "Know how to draw Lewis structures for common molecules",
          "Understand WHY different bond types have different properties",
          "Be able to predict properties from bond type",
          "Remember: ionic = high MP, conducts when molten; covalent = low MP, doesn't conduct"
        ]
      },
      {
        id: "3",
        title: "Properties Related to Bonding",
        objectives: [
          "Predict physical properties from structure",
          "Understand intermolecular forces",
          "Compare different types of structures"
        ],
        keyTerms: [
          { term: "Intermolecular Forces", definition: "Weak attractions between molecules, not within molecules." },
          { term: "Van der Waals Forces", definition: "Weakest intermolecular force, present in all molecules." },
          { term: "Hydrogen Bonding", definition: "Strong intermolecular force when H bonded to F, O, or N." },
          { term: "Dipole-Dipole", definition: "Attraction between polar molecules." },
          { term: "Giant Structure", definition: "Large network of atoms bonded throughout structure." },
          { term: "Simple Molecular", definition: "Small molecules with weak forces between them." }
        ],
        studyNotes: `
## Properties Related to Bonding

### Structure Types

#### 1. Simple Molecular Structures
- Small, discrete molecules
- Strong covalent bonds within molecule
- Weak intermolecular forces between molecules
- Low melting/boiling points
- Examples: H₂O, CO₂, I₂, sugar

#### 2. Giant Covalent Structures
- Billions of atoms bonded in network
- No separate molecules
- Very high melting/boiling points
- Hard (usually)
- Examples: Diamond, graphite, silicon dioxide

#### 3. Giant Ionic Structures
- Regular lattice of ions
- Strong electrostatic attractions
- High melting/boiling points
- Conduct when molten/dissolved
- Examples: NaCl, MgO

#### 4. Giant Metallic Structures
- Regular lattice of metal ions
- Sea of delocalized electrons
- Conduct electricity
- Malleable and ductile
- Examples: All metals

---

### Intermolecular Forces

#### Van der Waals Forces (London Dispersion)
- Present in ALL molecules
- Caused by temporary dipoles
- Increases with molecular size
- Weakest intermolecular force

#### Dipole-Dipole Forces
- Between polar molecules
- Partial charges attract
- Stronger than van der Waals

#### Hydrogen Bonding
- Special case of dipole-dipole
- H bonded to F, O, or N
- Strongest intermolecular force
- Explains water's high boiling point

### Water's Special Properties
Water has unusually high melting and boiling points due to hydrogen bonding:
- Ice floats (hydrogen bonds create open structure)
- High surface tension
- High specific heat capacity
        `,
        practiceQuestions: [
          "What are intermolecular forces? How are they different from chemical bonds?",
          "Name three types of intermolecular forces in order of strength.",
          "Why does water have a higher boiling point than methane?",
          "Compare the structures of diamond and graphite. Explain their different properties.",
          "Why do ionic compounds have high melting points?"
        ]
      }
    ]
  },
  strand2: {
    title: "Quantitative and Aqueous Chemistry",
    overview: "Mole calculations, stoichiometry, and solution chemistry.",
    image: noteImages.labBeakers,
    units: [
      {
        id: "1",
        title: "The Mole Concept",
        image: noteImages.chemFlasks,
        objectives: [
          "Understand Avogadro's number and the mole",
          "Calculate molar mass",
          "Convert between moles, mass, and particles",
          "Perform stoichiometric calculations"
        ],
        keyTerms: [
          { term: "Mole", definition: "Amount of substance containing 6.022 × 10²³ particles. SI unit: mol." },
          { term: "Avogadro's Number", definition: "6.022 × 10²³ - number of particles in one mole." },
          { term: "Molar Mass", definition: "Mass of one mole of a substance, in grams per mole (g/mol)." },
          { term: "Stoichiometry", definition: "Calculation of quantities in chemical reactions using mole ratios." },
          { term: "Limiting Reagent", definition: "Reactant that runs out first, limiting the amount of product." },
          { term: "Excess Reagent", definition: "Reactant that remains after reaction is complete." },
          { term: "Percentage Yield", definition: "Actual yield divided by theoretical yield, times 100%." }
        ],
        studyNotes: `
## The Mole Concept

### What is a Mole?
A mole is a counting unit for chemistry - like a "dozen" but for atoms and molecules.

**1 mole = 6.022 × 10²³ particles** (Avogadro's number)

This number was chosen so that:
- 1 mole of carbon-12 = exactly 12 grams
- 1 mole of any element (in g) = its atomic mass

---

## Key Formulas

### Moles, Mass, and Molar Mass:
\`\`\`
n = m / M

Where:
n = number of moles (mol)
m = mass (g)
M = molar mass (g/mol)
\`\`\`

### Moles and Particles:
\`\`\`
Number of particles = n × Nₐ

Where:
Nₐ = 6.022 × 10²³ (Avogadro's number)
\`\`\`

### Molar Mass Calculation:
Add atomic masses of all atoms in formula.

**Example: H₂SO₄**
- H: 1 × 2 = 2
- S: 32 × 1 = 32
- O: 16 × 4 = 64
- Total = 98 g/mol

---

## Calculation Examples

### Example 1: Find moles from mass
*How many moles in 36 g of water (H₂O)?*

Molar mass of H₂O = (2×1) + 16 = 18 g/mol
n = m/M = 36/18 = **2 mol**

### Example 2: Find mass from moles
*What is the mass of 0.5 mol of CO₂?*

Molar mass of CO₂ = 12 + (2×16) = 44 g/mol
m = n × M = 0.5 × 44 = **22 g**

### Example 3: Find number of particles
*How many molecules in 2 mol of O₂?*

Particles = n × Nₐ = 2 × 6.022 × 10²³
= **1.204 × 10²⁴ molecules**

---

## Stoichiometry

Using balanced equations to calculate quantities.

### Example:
2H₂ + O₂ → 2H₂O

*How many moles of H₂O produced from 4 mol H₂?*

From equation: 2 mol H₂ produces 2 mol H₂O
Ratio is 1:1
So 4 mol H₂ produces **4 mol H₂O**

### Percentage Yield:
\`\`\`
% Yield = (Actual yield / Theoretical yield) × 100%
\`\`\`
        `,
        practiceQuestions: [
          "What is a mole? What is Avogadro's number?",
          "Calculate the molar mass of: (a) NaCl (b) H₂SO₄ (c) Ca(OH)₂",
          "How many moles in 88 g of CO₂?",
          "What is the mass of 3 mol of NaOH?",
          "How many molecules are in 0.5 mol of water?",
          "In the reaction N₂ + 3H₂ → 2NH₃, how many moles of NH₃ are produced from 6 mol H₂?",
          "Calculate the percentage yield if 36 g of water was obtained when 40 g was expected.",
          "What is a limiting reagent? Why is it important?"
        ]
      }
    ]
  },
  strand3: {
    title: "Physical Chemistry",
    overview: "Energy changes, reaction rates, and chemical equilibrium.",
    image: noteImages.chemExperiment,
    units: []
  },
  strand4: {
    title: "Oxidation and Reduction",
    overview: "Redox reactions and electrochemistry.",
    image: noteImages.labEquipment,
    units: []
  },
  strand5: {
    title: "Organic Chemistry",
    overview: "Carbon compounds and organic reactions.",
    image: noteImages.labBeakers,
    units: []
  }
};

// Year 12 and Year 13 Chemistry Notes (abbreviated - can be expanded)
export const year12ChemistryStudentNotes = {
  strand1: {
    title: "Atomic Structure, Bonding and Related Properties",
    overview: "Advanced study of chemical reactions, stoichiometry calculations, and reaction types.",
    image: noteImages.atomStructure,
    units: [
      {
        id: "1",
        title: "Stoichiometry",
        image: noteImages.molecules,
        objectives: [
          "Perform mole calculations for chemical reactions",
          "Balance complex chemical equations",
          "Calculate limiting reagents and theoretical yield",
          "Understand percentage yield and purity",
          "Apply stoichiometry to real-world problems"
        ],
        keyTerms: [
          { term: "Stoichiometry", definition: "The calculation of quantities in chemical reactions using balanced equations." },
          { term: "Mole Ratio", definition: "The ratio of moles of reactants and products in a balanced equation." },
          { term: "Limiting Reagent", definition: "The reactant that is completely consumed first, limiting the amount of product formed." },
          { term: "Excess Reagent", definition: "The reactant that remains after the reaction is complete." },
          { term: "Theoretical Yield", definition: "The maximum amount of product that can be formed from given amounts of reactants." },
          { term: "Actual Yield", definition: "The amount of product actually obtained from a reaction." },
          { term: "Percentage Yield", definition: "The ratio of actual yield to theoretical yield, expressed as a percentage." },
          { term: "Molar Mass", definition: "The mass of one mole of a substance, in grams per mole (g/mol)." }
        ],
        studyNotes: `
## Stoichiometry

### The Mole Concept Review
- 1 mole = 6.022 × 10²³ particles (Avogadro's number)
- Molar mass = mass of 1 mole of substance in grams

### Stoichiometric Calculations
1. Write balanced equation
2. Convert given quantity to moles
3. Use mole ratio from equation
4. Convert to required units

### Example Problem
2H₂ + O₂ → 2H₂O
If 4 moles of H₂ react:
- Mole ratio H₂:H₂O = 2:2 = 1:1
- Therefore, 4 moles H₂O produced

### Limiting Reagent
- Compare mole ratios of reactants
- The one that runs out first limits the reaction
        `,
        practiceQuestions: [
          "Calculate the mass of water produced when 10g of hydrogen reacts with excess oxygen.",
          "What is the limiting reagent when 5 mol of N₂ reacts with 12 mol of H₂?",
          "If a reaction has 85% yield, what mass of product is obtained from a theoretical yield of 50g?",
          "Balance: Fe₂O₃ + CO → Fe + CO₂",
          "How many moles of CO₂ are produced from 2 moles of C₆H₁₂O₆?"
        ],
        examTips: [
          "Always start by writing and balancing the equation",
          "Show all unit conversions clearly",
          "Check your answer makes sense (products can't weigh more than reactants)"
        ]
      },
      {
        id: "2",
        title: "Thermochemistry",
        image: noteImages.atomStructure,
        objectives: [
          "Understand enthalpy and enthalpy changes",
          "Calculate heat energy using q = mcΔT",
          "Interpret and draw enthalpy diagrams",
          "Apply Hess's Law for enthalpy calculations",
          "Understand bond energies and their applications"
        ],
        keyTerms: [
          { term: "Enthalpy (H)", definition: "The total heat content of a system at constant pressure." },
          { term: "Exothermic", definition: "A reaction that releases heat to the surroundings. ΔH is negative." },
          { term: "Endothermic", definition: "A reaction that absorbs heat from the surroundings. ΔH is positive." },
          { term: "Specific Heat Capacity", definition: "The energy required to raise 1g of substance by 1°C." },
          { term: "Hess's Law", definition: "The total enthalpy change is independent of the route taken." },
          { term: "Bond Energy", definition: "The energy required to break one mole of bonds in gaseous molecules." },
          { term: "Activation Energy", definition: "The minimum energy required for a reaction to occur." }
        ],
        studyNotes: `
## Thermochemistry

### Heat Energy Calculations
q = mcΔT
- q = heat energy (J)
- m = mass (g)
- c = specific heat capacity (J/g°C)
- ΔT = temperature change (°C)

### Enthalpy Changes
- ΔH = H(products) - H(reactants)
- Exothermic: ΔH < 0 (releases heat)
- Endothermic: ΔH > 0 (absorbs heat)

### Hess's Law
The enthalpy change of a reaction is the same regardless of the pathway taken.

### Bond Energy Calculations
ΔH = Σ(bonds broken) - Σ(bonds formed)
- Breaking bonds requires energy (positive)
- Forming bonds releases energy (negative)
        `,
        practiceQuestions: [
          "Calculate the heat released when 100g of water cools from 80°C to 25°C.",
          "Draw an enthalpy diagram for an exothermic reaction.",
          "Use Hess's Law to calculate ΔH for: C + O₂ → CO₂",
          "Calculate ΔH using bond energies for: H₂ + Cl₂ → 2HCl",
          "Explain why breaking bonds is endothermic."
        ],
        examTips: [
          "Remember: exothermic = negative ΔH, endothermic = positive ΔH",
          "In Hess's Law, reverse reactions have opposite signs",
          "Bond energies are always positive values"
        ]
      },
      {
        id: "3",
        title: "Acids, Bases, and Salts",
        image: noteImages.molecules,
        objectives: [
          "Define acids and bases using different theories",
          "Calculate pH and understand the pH scale",
          "Perform acid-base titration calculations",
          "Understand buffer solutions",
          "Identify common acid-base indicators"
        ],
        keyTerms: [
          { term: "Acid", definition: "A substance that donates H⁺ ions (protons) in solution." },
          { term: "Base", definition: "A substance that accepts H⁺ ions or donates OH⁻ ions." },
          { term: "pH", definition: "A measure of hydrogen ion concentration: pH = -log[H⁺]." },
          { term: "Neutralization", definition: "Reaction between acid and base producing salt and water." },
          { term: "Buffer Solution", definition: "A solution that resists changes in pH when small amounts of acid or base are added." },
          { term: "Titration", definition: "A technique to determine concentration by reacting with a standard solution." },
          { term: "Indicator", definition: "A substance that changes color at different pH values." }
        ],
        studyNotes: `
## Acids, Bases, and Salts

### Acid-Base Theories
1. **Arrhenius**: Acids produce H⁺, bases produce OH⁻
2. **Brønsted-Lowry**: Acids donate H⁺, bases accept H⁺
3. **Lewis**: Acids accept electrons, bases donate electrons

### pH Scale
- pH = -log[H⁺]
- pH 7 = neutral
- pH < 7 = acidic
- pH > 7 = basic/alkaline

### Titration Calculations
At equivalence point:
moles of acid = moles of base (for 1:1 ratio)
M₁V₁ = M₂V₂

### Common Indicators
- Litmus: Red in acid, blue in base
- Phenolphthalein: Colorless in acid, pink in base
- Methyl orange: Red in acid, yellow in base
        `,
        practiceQuestions: [
          "Calculate the pH of a 0.01 M HCl solution.",
          "What volume of 0.5 M NaOH neutralizes 25 mL of 0.2 M HCl?",
          "Explain why a buffer resists pH changes.",
          "Identify the conjugate acid-base pairs in: NH₃ + H₂O ⇌ NH₄⁺ + OH⁻",
          "Calculate [H⁺] for a solution with pH 4.5"
        ],
        examTips: [
          "Remember: pH + pOH = 14 at 25°C",
          "Strong acids/bases completely dissociate",
          "Always show the balanced equation for neutralization"
        ]
      }
    ]
  },
  strand2: {
    title: "Quantitative and Aqueous Chemistry",
    overview: "Introduction to organic compounds, functional groups, and reactions.",
    image: noteImages.molecules,
    units: [
      {
        id: "4",
        title: "Introduction to Organic Chemistry",
        image: noteImages.molecules,
        objectives: [
          "Understand the unique properties of carbon",
          "Name organic compounds using IUPAC nomenclature",
          "Identify functional groups",
          "Draw structural and skeletal formulas",
          "Understand isomerism"
        ],
        keyTerms: [
          { term: "Hydrocarbon", definition: "A compound containing only carbon and hydrogen atoms." },
          { term: "Functional Group", definition: "An atom or group of atoms that determines the chemical properties of a compound." },
          { term: "Homologous Series", definition: "A family of compounds with the same functional group and general formula." },
          { term: "Isomers", definition: "Compounds with the same molecular formula but different structural arrangements." },
          { term: "Saturated", definition: "Contains only single bonds between carbon atoms." },
          { term: "Unsaturated", definition: "Contains at least one double or triple bond between carbon atoms." }
        ],
        studyNotes: `
## Introduction to Organic Chemistry

### Why Carbon is Special
- Forms 4 covalent bonds
- Can form chains, rings, and branches
- Bonds with itself and other elements

### Homologous Series
1. **Alkanes**: CₙH₂ₙ₊₂ (single bonds only)
2. **Alkenes**: CₙH₂ₙ (contains C=C double bond)
3. **Alkynes**: CₙH₂ₙ₋₂ (contains C≡C triple bond)
4. **Alcohols**: Contains -OH group
5. **Carboxylic Acids**: Contains -COOH group

### IUPAC Naming
1. Find longest carbon chain
2. Number from end nearest functional group
3. Name substituents with position numbers
4. Use prefixes: meth-, eth-, prop-, but-, pent-
        `,
        practiceQuestions: [
          "Draw the structural formula of 2-methylbutane.",
          "Name: CH₃CH₂CH(CH₃)CH₂CH₃",
          "How many isomers does C₄H₁₀ have? Draw them.",
          "Identify the functional group in ethanol.",
          "Write the general formula for alkenes."
        ],
        examTips: [
          "Always draw out the structure to help with naming",
          "Check carbon has 4 bonds in all structures",
          "Isomers have same molecular formula but different structures"
        ]
      }
    ]
  },
  strand3: {
    title: "Physical Chemistry",
    overview: "Study of energy changes, reaction rates, and equilibrium.",
    image: noteImages.atomStructure,
    units: [
      {
        id: "5",
        title: "Thermochemistry",
        objectives: ["Understand enthalpy changes", "Calculate heat energy", "Apply Hess's Law"],
        keyTerms: [
          { term: "Enthalpy", definition: "The total heat content of a system at constant pressure." },
          { term: "Exothermic", definition: "A reaction that releases heat (ΔH < 0)." },
          { term: "Endothermic", definition: "A reaction that absorbs heat (ΔH > 0)." }
        ],
        studyNotes: "Thermochemistry involves studying energy changes in reactions. Exothermic reactions release heat, endothermic reactions absorb heat.",
        practiceQuestions: ["Calculate ΔH for the combustion of methane.", "Explain Hess's Law with an example."]
      }
    ]
  },
  strand4: {
    title: "Oxidation and Reduction",
    overview: "Understanding electron transfer in chemical reactions.",
    image: noteImages.molecules,
    units: [
      {
        id: "6",
        title: "Redox Reactions",
        objectives: ["Define oxidation and reduction", "Assign oxidation states", "Balance redox equations"],
        keyTerms: [
          { term: "Oxidation", definition: "Loss of electrons." },
          { term: "Reduction", definition: "Gain of electrons." },
          { term: "Oxidizing Agent", definition: "A substance that causes oxidation by gaining electrons." }
        ],
        studyNotes: "OIL RIG: Oxidation Is Loss, Reduction Is Gain of electrons.",
        practiceQuestions: ["Identify the oxidizing and reducing agents in: Zn + Cu²⁺ → Zn²⁺ + Cu"]
      }
    ]
  },
  strand5: {
    title: "Organic Chemistry",
    overview: "Study of carbon-based compounds and their reactions.",
    image: noteImages.molecules,
    units: [
      {
        id: "7",
        title: "Hydrocarbons",
        objectives: ["Name alkanes, alkenes, alkynes", "Understand isomerism", "Describe properties"],
        keyTerms: [
          { term: "Alkane", definition: "Saturated hydrocarbon with single bonds only (CₙH₂ₙ₊₂)." },
          { term: "Alkene", definition: "Unsaturated hydrocarbon with C=C double bond (CₙH₂ₙ)." }
        ],
        studyNotes: "Hydrocarbons are compounds of carbon and hydrogen. Alkanes are saturated, alkenes and alkynes are unsaturated.",
        practiceQuestions: ["Draw and name all isomers of C₄H₁₀.", "Compare properties of alkanes and alkenes."]
      }
    ]
  }
};

export const year13ChemistryStudentNotes = {
  strand1: {
    title: "Atomic Structure, Bonding and Related Properties",
    overview: "In-depth study of chemical equilibrium, kinetics, and electrochemistry for national examination preparation.",
    image: noteImages.molecules,
    units: [
      {
        id: "1",
        title: "Chemical Equilibrium",
        image: noteImages.atomStructure,
        objectives: [
          "Understand dynamic equilibrium",
          "Write and calculate equilibrium constants (Kc, Kp)",
          "Apply Le Chatelier's Principle",
          "Calculate equilibrium concentrations",
          "Understand the relationship between Kc and Kp"
        ],
        keyTerms: [
          { term: "Dynamic Equilibrium", definition: "A state where forward and reverse reactions occur at equal rates, with no net change in concentrations." },
          { term: "Equilibrium Constant (Kc)", definition: "The ratio of product concentrations to reactant concentrations at equilibrium, each raised to their stoichiometric powers." },
          { term: "Le Chatelier's Principle", definition: "When a system at equilibrium is disturbed, it shifts to counteract the change." },
          { term: "Kp", definition: "Equilibrium constant expressed in terms of partial pressures for gaseous reactions." },
          { term: "Reaction Quotient (Q)", definition: "The ratio of concentrations at any point, used to predict direction of reaction." },
          { term: "Position of Equilibrium", definition: "Whether equilibrium lies to the left (reactants favored) or right (products favored)." }
        ],
        studyNotes: `
## Chemical Equilibrium

### Dynamic Equilibrium
- Forward rate = Reverse rate
- Concentrations remain constant
- Requires closed system

### Equilibrium Constant Expression
For: aA + bB ⇌ cC + dD

Kc = [C]^c[D]^d / [A]^a[B]^b

### Le Chatelier's Principle
System shifts to oppose changes:
- **Add reactant**: Shifts right (toward products)
- **Remove product**: Shifts right
- **Increase pressure**: Shifts toward fewer moles of gas
- **Increase temperature**: 
  - Exothermic: Shifts left
  - Endothermic: Shifts right

### Kc vs Kp Relationship
Kp = Kc(RT)^Δn
where Δn = moles gas products - moles gas reactants
        `,
        practiceQuestions: [
          "Write the Kc expression for: N₂(g) + 3H₂(g) ⇌ 2NH₃(g)",
          "If Kc = 4.0 × 10⁻², is the reaction product or reactant favored?",
          "Predict the effect of increasing pressure on: 2SO₂(g) + O₂(g) ⇌ 2SO₃(g)",
          "Calculate Kc if [N₂] = 0.1 M, [H₂] = 0.3 M, [NH₃] = 0.2 M at equilibrium",
          "How does a catalyst affect the position of equilibrium?"
        ],
        examTips: [
          "Pure solids and liquids are NOT included in Kc expressions",
          "Kc has no units when calculated correctly in many cases",
          "Temperature is the ONLY factor that changes the value of K"
        ]
      },
      {
        id: "2",
        title: "Chemical Kinetics",
        image: noteImages.molecules,
        objectives: [
          "Understand factors affecting reaction rate",
          "Interpret and draw rate graphs",
          "Determine rate laws and orders of reaction",
          "Calculate rate constants",
          "Understand reaction mechanisms and rate-determining steps"
        ],
        keyTerms: [
          { term: "Reaction Rate", definition: "The change in concentration of a reactant or product per unit time." },
          { term: "Rate Law", definition: "An equation relating reaction rate to concentrations of reactants." },
          { term: "Order of Reaction", definition: "The power to which a concentration is raised in the rate law." },
          { term: "Rate Constant (k)", definition: "A proportionality constant in the rate law, dependent on temperature." },
          { term: "Half-life (t½)", definition: "The time taken for the concentration of a reactant to halve." },
          { term: "Activation Energy", definition: "The minimum energy required for a successful collision." },
          { term: "Catalyst", definition: "A substance that increases reaction rate without being consumed." }
        ],
        studyNotes: `
## Chemical Kinetics

### Rate of Reaction
Rate = -Δ[reactant]/Δt = +Δ[product]/Δt

### Factors Affecting Rate
1. **Concentration**: Higher = faster (more collisions)
2. **Temperature**: Higher = faster (more energy)
3. **Surface Area**: Larger = faster (more contact)
4. **Catalyst**: Lowers activation energy

### Rate Laws
Rate = k[A]^m[B]^n
- m, n = orders (determined experimentally)
- Overall order = m + n

### Half-life for First Order
t½ = ln(2)/k = 0.693/k
(Half-life is constant for first order)

### Arrhenius Equation
k = Ae^(-Ea/RT)
ln(k) = ln(A) - Ea/RT
        `,
        practiceQuestions: [
          "If doubling [A] quadruples the rate, what is the order with respect to A?",
          "Calculate the rate constant if rate = 2.5 × 10⁻³ M/s when [A] = 0.5 M (first order)",
          "A first-order reaction has k = 0.02 s⁻¹. Calculate the half-life.",
          "Sketch a graph of [A] vs time for zero, first, and second order reactions",
          "Explain how a catalyst increases reaction rate using collision theory"
        ],
        examTips: [
          "Order must be determined experimentally, not from the equation",
          "Rate constant k changes with temperature",
          "For first order: half-life is independent of concentration"
        ]
      },
      {
        id: "3",
        title: "Electrochemistry",
        image: noteImages.atomStructure,
        objectives: [
          "Understand oxidation and reduction in terms of electron transfer",
          "Balance redox equations using half-reactions",
          "Calculate cell potential using standard electrode potentials",
          "Understand electrolysis and Faraday's laws",
          "Apply electrochemistry to batteries and corrosion"
        ],
        keyTerms: [
          { term: "Oxidation", definition: "Loss of electrons (increase in oxidation state)." },
          { term: "Reduction", definition: "Gain of electrons (decrease in oxidation state)." },
          { term: "Oxidizing Agent", definition: "A substance that causes oxidation by accepting electrons." },
          { term: "Reducing Agent", definition: "A substance that causes reduction by donating electrons." },
          { term: "Standard Electrode Potential (E°)", definition: "The potential of a half-cell compared to the standard hydrogen electrode." },
          { term: "Cell Potential (E°cell)", definition: "The voltage of an electrochemical cell: E°cell = E°cathode - E°anode" },
          { term: "Electrolysis", definition: "Using electricity to drive a non-spontaneous redox reaction." }
        ],
        studyNotes: `
## Electrochemistry

### Redox Definitions
- **OIL RIG**: Oxidation Is Loss, Reduction Is Gain (of electrons)
- Oxidation state increases = oxidation
- Oxidation state decreases = reduction

### Balancing Redox Equations
1. Separate into half-reactions
2. Balance atoms (except O and H)
3. Balance O with H₂O
4. Balance H with H⁺
5. Balance charge with electrons
6. Combine half-reactions

### Cell Potential
E°cell = E°cathode - E°anode
- If E°cell > 0, reaction is spontaneous
- If E°cell < 0, reaction is non-spontaneous

### Faraday's Laws of Electrolysis
Q = It (charge = current × time)
moles of electrons = Q/F (F = 96,485 C/mol)
        `,
        practiceQuestions: [
          "Balance: MnO₄⁻ + Fe²⁺ → Mn²⁺ + Fe³⁺ (in acidic solution)",
          "Calculate E°cell for: Zn + Cu²⁺ → Zn²⁺ + Cu (E°Zn = -0.76V, E°Cu = +0.34V)",
          "How long must a current of 2A flow to deposit 3.2g of Cu? (Cu = 64)",
          "Identify the oxidizing and reducing agents in: 2Na + Cl₂ → 2NaCl",
          "Why does the anode dissolve during electrolysis of copper sulfate with copper electrodes?"
        ],
        examTips: [
          "More positive E° = better oxidizing agent (more likely to be reduced)",
          "In electrolysis: anode = oxidation, cathode = reduction",
          "Always check electron balance when combining half-reactions"
        ]
      }
    ]
  },
  strand2: {
    title: "Quantitative and Aqueous Chemistry",
    overview: "Complex organic reactions, mechanisms, and synthesis for national examination.",
    image: noteImages.molecules,
    units: [
      {
        id: "4",
        title: "Organic Reaction Mechanisms",
        image: noteImages.molecules,
        objectives: [
          "Understand nucleophilic substitution (SN1 and SN2)",
          "Understand electrophilic addition to alkenes",
          "Draw curly arrow mechanisms",
          "Predict products of organic reactions",
          "Compare reaction mechanisms and conditions"
        ],
        keyTerms: [
          { term: "Nucleophile", definition: "An electron-rich species that donates an electron pair to form a bond." },
          { term: "Electrophile", definition: "An electron-deficient species that accepts an electron pair." },
          { term: "SN1", definition: "Unimolecular nucleophilic substitution - two-step mechanism via carbocation." },
          { term: "SN2", definition: "Bimolecular nucleophilic substitution - one-step concerted mechanism." },
          { term: "Carbocation", definition: "A positively charged carbon intermediate." },
          { term: "Curly Arrow", definition: "Shows movement of an electron pair in a mechanism." }
        ],
        studyNotes: `
## Organic Reaction Mechanisms

### Nucleophilic Substitution
**SN2 (one step)**:
- Nucleophile attacks as leaving group leaves
- Inversion of configuration
- Favored by: primary substrates, strong nucleophiles

**SN1 (two steps)**:
1. Leaving group leaves → carbocation
2. Nucleophile attacks
- Racemization occurs
- Favored by: tertiary substrates, weak nucleophiles

### Electrophilic Addition to Alkenes
1. π bond attacks electrophile
2. Carbocation intermediate forms
3. Nucleophile attacks carbocation

Example: Br₂ + CH₂=CH₂ → CH₂Br-CH₂Br

### Markovnikov's Rule
H adds to carbon with MORE hydrogens
(Carbocation stability: 3° > 2° > 1°)
        `,
        practiceQuestions: [
          "Draw the mechanism for: CH₃Br + OH⁻ → CH₃OH + Br⁻",
          "Predict the product of HBr addition to propene using Markovnikov's rule",
          "Compare SN1 and SN2 mechanisms for tertiary vs primary halogenoalkanes",
          "Draw curly arrows for electrophilic addition of Br₂ to ethene",
          "Explain why tertiary carbocations are more stable than primary"
        ],
        examTips: [
          "Curly arrows start from electron-rich areas",
          "Show ALL curly arrows and intermediates in mechanisms",
          "Remember: SN2 = one step, SN1 = two steps"
        ]
      },
      {
        id: "5",
        title: "Polymers and Biochemistry",
        image: noteImages.molecules,
        objectives: [
          "Understand addition and condensation polymerization",
          "Identify monomers from polymer structures",
          "Understand protein and carbohydrate structures",
          "Describe the importance of polymers in everyday life",
          "Understand biodegradability and environmental impact"
        ],
        keyTerms: [
          { term: "Polymer", definition: "A large molecule made of repeating monomer units." },
          { term: "Monomer", definition: "A small molecule that joins with others to form a polymer." },
          { term: "Addition Polymer", definition: "Formed when unsaturated monomers join without loss of atoms." },
          { term: "Condensation Polymer", definition: "Formed when monomers join with elimination of small molecules (usually water)." },
          { term: "Amino Acid", definition: "Monomer of proteins, containing -NH₂ and -COOH groups." },
          { term: "Peptide Bond", definition: "The C-N bond formed between amino acids in proteins." }
        ],
        studyNotes: `
## Polymers and Biochemistry

### Addition Polymerization
- Requires C=C double bond
- No atoms lost
- Example: ethene → poly(ethene)
  nCH₂=CH₂ → -(CH₂-CH₂)n-

### Condensation Polymerization
- Two functional groups react
- Small molecule eliminated (usually H₂O)
- Examples: polyesters, polyamides (nylon)

### Proteins
- Polymers of amino acids
- Linked by peptide bonds (-CO-NH-)
- Primary structure: sequence of amino acids
- Secondary: α-helix, β-sheet
- Tertiary: 3D folding
- Quaternary: multiple chains

### Carbohydrates
- Monosaccharides: glucose, fructose
- Disaccharides: sucrose, maltose
- Polysaccharides: starch, cellulose
        `,
        practiceQuestions: [
          "Draw the repeating unit of poly(propene)",
          "What type of polymerization produces nylon? What is eliminated?",
          "Draw the structure formed when two amino acids join",
          "Why is cellulose not digestible by humans but starch is?",
          "Explain why some polymers are biodegradable and others are not"
        ],
        examTips: [
          "For addition polymers: open the double bond to show the repeat unit",
          "Condensation polymers need TWO different functional groups",
          "Peptide bonds form between -COOH of one amino acid and -NH₂ of another"
        ]
      }
    ]
  },
  strand3: {
    title: "Physical Chemistry",
    overview: "Advanced study of equilibrium, kinetics, and thermodynamics.",
    image: noteImages.atomStructure,
    units: [
      {
        id: "6",
        title: "Chemical Equilibrium",
        objectives: ["Apply Le Chatelier's Principle", "Calculate Kc and Kp", "Understand equilibrium shifts"],
        keyTerms: [
          { term: "Equilibrium Constant (Kc)", definition: "Ratio of product to reactant concentrations at equilibrium." },
          { term: "Le Chatelier's Principle", definition: "System shifts to counteract applied stress." }
        ],
        studyNotes: "At equilibrium, forward and reverse rates are equal. K only changes with temperature.",
        practiceQuestions: ["Write Kc expression for N₂ + 3H₂ ⇌ 2NH₃", "Predict equilibrium shift when pressure increases."]
      }
    ]
  },
  strand4: {
    title: "Oxidation and Reduction",
    overview: "Advanced electrochemistry and redox reactions.",
    image: noteImages.molecules,
    units: [
      {
        id: "7",
        title: "Electrochemistry",
        objectives: ["Calculate cell potentials", "Understand electrolysis", "Apply Faraday's laws"],
        keyTerms: [
          { term: "Cell Potential (E°)", definition: "Voltage of an electrochemical cell." },
          { term: "Electrolysis", definition: "Using electricity to drive non-spontaneous reactions." }
        ],
        studyNotes: "E°cell = E°cathode - E°anode. Positive E° means spontaneous reaction.",
        practiceQuestions: ["Calculate E° for Zn/Cu cell.", "How much Cu deposits with 2A for 1 hour?"]
      }
    ]
  },
  strand5: {
    title: "Organic Chemistry",
    overview: "Advanced organic reactions, mechanisms, and synthesis.",
    image: noteImages.molecules,
    units: [
      {
        id: "8",
        title: "Reaction Mechanisms",
        objectives: ["Draw SN1/SN2 mechanisms", "Understand elimination reactions", "Predict products"],
        keyTerms: [
          { term: "Nucleophile", definition: "Electron-rich species that attacks positive centers." },
          { term: "Electrophile", definition: "Electron-deficient species that accepts electrons." }
        ],
        studyNotes: "SN2: one step, backside attack, inversion. SN1: two steps via carbocation, racemization.",
        practiceQuestions: ["Draw mechanism for CH₃Br + OH⁻", "Predict SN1 vs SN2 for tertiary halide."]
      }
    ]
  }
};

// ============================================
// EXPORT ALL NOTES
// ============================================
export default {
  ict: {
    year11: year11ICTStudentNotes,
    year12: year12ICTStudentNotes,
    year13: year13ICTStudentNotes
  },
  chemistry: {
    year11: year11ChemistryStudentNotes,
    year12: year12ChemistryStudentNotes,
    year13: year13ChemistryStudentNotes
  },
  images: noteImages
};
